import { a as sortKeys, i as isNode, r as isBrowser, t as cleanFalsyValues } from "./utils-B7chC9_U.js";
import { authTypesThatCanBeOmitted, hostConfigCommonProperties } from "../auth-types.js";

//#region src/platform/platform-functions.ts
const getPlatform = async (options = {}) => {
	const hc = resolveHostConfig(options.hostConfig);
	if (hc.authType === "mock-backend-rest-recorder") return hc.recordGetPlatform();
	if (hc?.authType === "mock-backend") return hc.mockGetPlatform();
	if (hc?.authType === "noauth") return result({ isUnknown: true });
	let productInfo = isBrowser() ? window.QlikMain?.PRODUCT_INFO : void 0;
	if (!productInfo) {
		const { data, status } = await getProductInfo(options);
		productInfo = data;
		if (status === 404) return result({ isUnknown: true });
		if (!productInfo || status <= 399 && status >= 300) return result({
			isQSE: true,
			isWindows: true
		});
	}
	const deploymentType = (productInfo.composition?.deploymentType || "").toLowerCase();
	if (deploymentType === "qliksenseserver") return result({
		isQSE: true,
		isWindows: true,
		meta: extractMeta(productInfo)
	});
	if (deploymentType === "qliksensedesktop") return result({
		isQSD: true,
		isWindows: true,
		meta: extractMeta(productInfo)
	});
	if (deploymentType === "qliksensemobile") return result({
		isQSE: true,
		isWindows: true,
		meta: extractMeta(productInfo)
	});
	if (deploymentType === "cloud-console") return result({
		isCloud: true,
		isCloudConsole: true,
		meta: extractMeta(productInfo)
	});
	if (productInfo.composition?.provider === "fedramp") return result({
		isCloud: true,
		isQCG: true,
		meta: extractMeta(productInfo)
	});
	return result({
		isCloud: true,
		isQCS: true,
		meta: extractMeta(productInfo)
	});
};
const productInfoPromises = {};
/**
* Retrieves the full complete url to the product-info file. The full URL
* is the cache key. Without templating the URL we'd cache `""` as an entry.
* @private
*/
function templateUrl(baseUrl) {
	return `${baseUrl}/resources/autogenerated/product-info.json`;
}
/**
* Retrieves the product information as a JSON object.
* It makes an HTTP GET request to fetch the autogenerated product-info.json file.
*
* `data` is `undefined` if the file can not be retrieved.
*/
const getProductInfo = async ({ hostConfig, noCache } = {}) => {
	const completeUrl = templateUrl(toValidLocationUrl(hostConfig));
	try {
		if (!(completeUrl in productInfoPromises)) {
			const fetchOptions = {};
			if (globalThis.QlikMain?.resourceNeedsCredentials(completeUrl)) fetchOptions.credentials = "include";
			productInfoPromises[completeUrl] = fetch(completeUrl, fetchOptions).then(async (res) => {
				if (res.ok) return {
					data: await res.json(),
					status: res.status
				};
				return {
					data: void 0,
					status: res.status
				};
			});
		}
		const response = await productInfoPromises[completeUrl];
		if (response.status >= 400 || !response.data) delete productInfoPromises[completeUrl];
		return response;
	} catch {
		delete productInfoPromises[completeUrl];
		return {
			data: void 0,
			status: 500
		};
	} finally {
		if (noCache) delete productInfoPromises[completeUrl];
	}
};
/** @internal */
const extractMeta = (data) => {
	const urls = data.externalUrls;
	if (!urls) return;
	const productName = data.composition?.productName ?? "Qlik";
	const releaseLabel = data.composition?.releaseLabel || "-";
	const productLabel = releaseLabel === "-" ? productName : `${productName} (${releaseLabel})`;
	return {
		productId: data.composition?.senseId ?? "qlik",
		productLabel,
		version: data.composition?.version,
		urls: {
			personalHelpBaseUrl: urls.personalHelpBaseUrl,
			personalUpgradeBase: urls.personalUpgradeBase,
			personalUpgradeUrl: urls.personalUpgradeUrl,
			serverHelpBaseUrl: urls.serverHelpBaseUrl,
			qlikWebPageUrl: urls.qlikWebPageUrl
		}
	};
};
const result = (data) => ({
	isCloud: false,
	isQCS: false,
	isQCG: false,
	isCloudConsole: false,
	isWindows: false,
	isQSE: false,
	isQSD: false,
	isUnknown: false,
	...data
});

//#endregion
//#region src/utils/random.ts
const NANOID_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-";
const HEX_ALPHABET = "0123456789abcdef";
/**
* Generates a random string from the given alphabet using crypto.getRandomValues
* Works in both browser and Node.js environments
*/
function generateRandomFromAlphabet(alphabet, length) {
	const bytes = new Uint8Array(length);
	globalThis.crypto.getRandomValues(bytes);
	let result = "";
	for (let i = 0; i < length; i++) result += alphabet[bytes[i] % alphabet.length];
	return result;
}
/**
* Method helper for generating a random string [a-zA-Z0-9\-_]{length}
* @param length - the length of the string
*/
function generateRandomString(targetLength) {
	return generateRandomFromAlphabet(NANOID_ALPHABET, targetLength);
}
/**
* Method helper for generating a random hexadecimal-string [0-9a-f]{length}
* @param length - the length of the string
*/
function generateRandomHexString(targetLength) {
	return generateRandomFromAlphabet(HEX_ALPHABET, targetLength);
}

//#endregion
//#region src/auth/internal/host-config-functions.ts
const emptyHostConfig = {};
const normalizedHostConfigs = /* @__PURE__ */ new Map();
/**
* Returns a new host config with all default and falsy values removed.
* @param hostConfig - The host config to fill with defaults
* @returns
*/
function removeDefaults(hostConfig) {
	const cleanedHostConfig = cleanFalsyValues(hostConfig) || {};
	if (cleanedHostConfig.host) cleanedHostConfig.host = toValidLocationUrl(cleanedHostConfig);
	if (isBrowser()) {
		if (toValidLocationUrl(cleanedHostConfig) === window.location.origin) delete cleanedHostConfig.host;
	}
	if (cleanedHostConfig.authType && authTypesThatCanBeOmitted.includes(cleanedHostConfig.authType)) delete cleanedHostConfig.authType;
	return cleanedHostConfig;
}
function globalReplacer(key, value) {
	if (typeof value === "function") return;
	return value;
}
/**
* Serializes the provided hostConfig, if present, otherwise the default one.
*/
function serializeHostConfig$1(hostConfig) {
	const sorted = sortKeys(removeDefaults(resolveHostConfig(hostConfig)));
	return JSON.stringify(sorted, globalReplacer);
}
const registeredHostConfigs = /* @__PURE__ */ new Map();
/**
* Registers a host config with the given name.
* @param name The name of the host config to be used to reference the host config later.
* @param hostConfig The host config to register.
*/
function registerHostConfig$1(name, hostConfig) {
	const reference = hostConfig?.reference || null;
	if (reference && !registeredHostConfigs.has(reference)) throw new InvalidHostConfigError(`Host config with reference "${reference}" is not registered. Please register it before using it.`);
	if (registeredHostConfigs.has(name)) console.warn(`registerHostConfig: Host config with name "${name}" is already registered. Overwriting.`);
	registeredHostConfigs.set(name, hostConfig);
}
/**
* Unregisters a host config with the given name.
* @param name The name of the host config to unregister.
*/
function unregisterHostConfig$1(name) {
	if (registeredHostConfigs.has(name)) registeredHostConfigs.delete(name);
	else console.warn(`unregisterHostConfig: Host config with name "${name}" not found.`);
}
/**
* Gets the host config with the given name.
* @private
* @param name The name of the host config to get.
* @returns The host config, or undefined if not found.
*/
function getRegisteredHostConfig(name) {
	return registeredHostConfigs.get(name);
}
/**
* Sets the default host config that will be used for all api calls that do not include a HostConfig
* @private
* @param hostConfig the default HostConfig to use
*/
function setDefaultHostConfig$1(hostConfig) {
	registerHostConfig$1("default", hostConfig || {});
}
/**
* Gets the default host config that will be used for all qmfe api calls that do not include a HostConfig.
* @private
* @returns The default host config that will be used for all qmfe api calls that do not include a HostConfig
*/
function getDefaultHostConfig$1() {
	return getRegisteredHostConfig("default") || {};
}
function normalizeHostConfig$1(hostConfig) {
	const suppliedHostConfigOrEmpty = hostConfig || emptyHostConfig;
	const serializedHostConfigKey = serializeHostConfig$1(suppliedHostConfigOrEmpty);
	let normalizedHostConfig = normalizedHostConfigs.get(serializedHostConfigKey);
	if (!normalizedHostConfig) {
		normalizedHostConfig = removeDefaults(resolveHostConfig(suppliedHostConfigOrEmpty));
		normalizedHostConfigs.set(serializedHostConfigKey, normalizedHostConfig);
	}
	return normalizedHostConfig;
}

//#endregion
//#region src/auth/internal/page-redirect-request-listeners.ts
/**
* A store containing all listener registries for different host configurations.
*/
const listenerRegistries$1 = /* @__PURE__ */ new Map();
/**
* Retrieves the listener registry for a given host configuration, creating one if it doesn't exist.
*/
function getRegistryForHostConfig(hostConfig) {
	const key = serializeHostConfig$1(hostConfig);
	let registry = listenerRegistries$1.get(key);
	if (!registry) {
		registry = {
			redirectRequestedListeners: /* @__PURE__ */ new Set(),
			redirectStartedListeners: /* @__PURE__ */ new Set()
		};
		listenerRegistries$1.set(key, registry);
	}
	return registry;
}
/**
* Registers a listener for page redirects requested by auth modules.
* @param hostConfig
* @param listener
* @returns
*/
function onPageRedirectRequested$1(hostConfig, listener) {
	const registry = getRegistryForHostConfig(hostConfig);
	registry.redirectRequestedListeners.add(listener);
	return () => {
		registry.redirectRequestedListeners.delete(listener);
	};
}
function onPageRedirectStarted$1(hostConfig, listener) {
	const registry = getRegistryForHostConfig(hostConfig);
	registry.redirectStartedListeners.add(listener);
	return () => {
		registry.redirectStartedListeners.delete(listener);
	};
}
function requestRedirect(hostConfig) {
	const registry = getRegistryForHostConfig(hostConfig);
	const hasRedirectListener = registry.redirectRequestedListeners.size > 0 || hostConfig.authRedirectUserConfirmation;
	if (hostConfig.autoRedirect || !hasRedirectListener) return Promise.resolve();
	if (typeof hostConfig.authRedirectUserConfirmation === "function") return hostConfig.authRedirectUserConfirmation().catch(() => {});
	return new Promise((resolve) => {
		const proceed = () => {
			for (const startedListener of registry.redirectStartedListeners) try {
				startedListener();
			} catch (error) {
				console.warn(error);
			}
			resolve();
		};
		for (const requestListener of registry.redirectRequestedListeners) try {
			requestListener({ proceed });
		} catch (error) {
			console.warn(error);
		}
	});
}

//#endregion
//#region src/utils/expose-internal-test-apis.ts
function exposeInternalApiOnWindow(name, fn) {
	if (globalThis.location?.origin.startsWith("https://localhost:") || globalThis.location?.origin?.endsWith("qlik-stage.com")) {
		if (globalThis.QlikMain) {
			if (globalThis.QlikMain.INTERNAL__DO_NOT_USE.qix === void 0) globalThis.QlikMain.INTERNAL__DO_NOT_USE.qix = {};
			globalThis.QlikMain.INTERNAL__DO_NOT_USE.qix[name] = fn;
		}
	}
}

//#endregion
//#region src/auth/internal/default-auth-modules/oauth/storage-helpers.ts
const storagePrefix = "qlik-qmfe-api";
function getTopicFromOauthHostConfig(hostConfig) {
	let topic = hostConfig.clientId + (hostConfig.scope ? `_${hostConfig.scope}` : "_user_default");
	if (hostConfig.subject) topic += `_${hostConfig.subject}`;
	if (hostConfig.userId) topic += `_${hostConfig.userId}`;
	return topic;
}
function getTopicFromAnonHostConfig(hostConfig) {
	return `${hostConfig.accessCode}_${hostConfig.clientId}`;
}
/**
* A map from oauth client id to promise of an access token
*/
const cachedTokens = {};
/**
* Used for testing.
*/
function clearAllCachedTokens() {
	for (const key in cachedTokens) delete cachedTokens[key];
}
exposeInternalApiOnWindow("clearAllAccessTokens", () => {
	console.log("Clearing tokens", cachedTokens);
	Object.keys(cachedTokens).forEach((key) => {
		console.log("Clearing access tokens for", key);
		deleteFromLocalStorage(key, ["access-token", "refresh-token"]);
		deleteFromSessionStorage(key, ["access-token", "refresh-token"]);
	});
	clearAllCachedTokens();
});
function saveInLocalStorage(topic, name, value) {
	localStorage.setItem(`${storagePrefix}-${topic}-${name}`, value);
}
function saveInSessionStorage(topic, name, value) {
	sessionStorage.setItem(`${storagePrefix}-${topic}-${name}`, value);
}
function saveInCustomSecretStorage(storage, topic, name, value) {
	return storage.store(`${storagePrefix}-${topic}-${name}`, value);
}
function loadFromLocalStorage(topic, name) {
	return localStorage.getItem(`${"qlik-qmfe-api"}-${topic}-${name}`) || void 0;
}
function loadFromSessionStorage(topic, name) {
	return sessionStorage.getItem(`${"qlik-qmfe-api"}-${topic}-${name}`) || void 0;
}
function loadFromCustomSecretStorage(storage, topic, name) {
	return storage.get(`${storagePrefix}-${topic}-${name}`);
}
function deleteFromLocalStorage(topic, names) {
	names.forEach((name) => localStorage.removeItem(`${storagePrefix}-${topic}-${name}`));
}
function deleteFromSessionStorage(topic, names) {
	names.forEach((name) => sessionStorage.removeItem(`${storagePrefix}-${topic}-${name}`));
}
function deleteFromCustomSecretStorage(storage, topic, names) {
	return Promise.all(names.map((name) => storage.delete(`${storagePrefix}-${topic}-${name}`)));
}
function loadAndDeleteFromSessionStorage(topic, name) {
	const id = `${storagePrefix}-${topic}-${name}`;
	const result = sessionStorage.getItem(id) || void 0;
	sessionStorage.removeItem(id);
	return result;
}
/**
* Returns true if the provided storage object implements the SecretStorage interface, false otherwise.
*/
function isCustomSecretStorage(storage) {
	return typeof storage === "object" && storage !== null && typeof storage.store === "function" && typeof storage.get === "function" && typeof storage.delete === "function";
}
async function loadOauthTokensFromStorage(topic, accessTokenStorage) {
	let accessToken;
	let refreshToken;
	if (accessTokenStorage === "local") {
		accessToken = loadFromLocalStorage(topic, "access-token");
		refreshToken = loadFromLocalStorage(topic, "refresh-token");
	} else if (accessTokenStorage === "session") {
		accessToken = loadFromSessionStorage(topic, "access-token");
		refreshToken = loadFromSessionStorage(topic, "refresh-token");
	} else if (isCustomSecretStorage(accessTokenStorage)) {
		accessToken = await loadFromCustomSecretStorage(accessTokenStorage, topic, "access-token");
		refreshToken = await loadFromCustomSecretStorage(accessTokenStorage, topic, "refresh-token");
	}
	if (accessToken) return {
		accessToken,
		refreshToken
	};
}
async function loadCachedOauthTokens(hostConfig) {
	return cachedTokens[getTopicFromOauthHostConfig(hostConfig)];
}
async function loadOrAcquireAccessTokenOauth(hostConfig, acquireTokens) {
	if (!hostConfig.clientId) throw new InvalidHostConfigError("A host config with authType set to \"oauth2\" has to also provide a clientId");
	return loadOrAcquireAccessToken(getTopicFromOauthHostConfig(hostConfig), acquireTokens, hostConfig.noCache, hostConfig.accessTokenStorage);
}
async function loadOrAcquireAccessTokenAnon(hostConfig, acquireTokens) {
	if (!hostConfig.accessCode) throw new InvalidHostConfigError("A host config with authType set to \"anonymous\" has to also provide an accessCode");
	return loadOrAcquireAccessToken(getTopicFromAnonHostConfig(hostConfig), acquireTokens, false, void 0);
}
async function loadOrAcquireAccessToken(topic, acquireTokens, noCache, accessTokenStorage) {
	if (noCache) return acquireTokens();
	cachedTokens[topic] = cachedTokens[topic] || (async () => {
		let tokens = await loadOauthTokensFromStorage(topic, accessTokenStorage);
		if (tokens) return tokens;
		tokens = await acquireTokens();
		if (accessTokenStorage === "local" && tokens) {
			if (tokens.accessToken) saveInLocalStorage(topic, "access-token", tokens.accessToken);
			if (tokens.refreshToken) saveInLocalStorage(topic, "refresh-token", tokens.refreshToken);
		} else if (accessTokenStorage === "session" && tokens) {
			if (tokens.accessToken) saveInSessionStorage(topic, "access-token", tokens.accessToken);
			if (tokens.refreshToken) saveInSessionStorage(topic, "refresh-token", tokens.refreshToken);
		} else if (isCustomSecretStorage(accessTokenStorage) && tokens) {
			if (tokens.accessToken) await saveInCustomSecretStorage(accessTokenStorage, topic, "access-token", tokens.accessToken);
			if (tokens.refreshToken) await saveInCustomSecretStorage(accessTokenStorage, topic, "refresh-token", tokens.refreshToken);
		}
		return tokens;
	})();
	return cachedTokens[topic];
}
function clearStoredOauthTokens(hostConfig) {
	const topic = getTopicFromOauthHostConfig(hostConfig);
	delete cachedTokens[topic];
	if (isBrowser()) {
		deleteFromLocalStorage(topic, ["access-token", "refresh-token"]);
		deleteFromSessionStorage(topic, ["access-token", "refresh-token"]);
	}
	if (isCustomSecretStorage(hostConfig.accessTokenStorage)) deleteFromCustomSecretStorage(hostConfig.accessTokenStorage, topic, ["access-token", "refresh-token"]);
}
function clearStoredAnonymousTokens(hostConfig) {
	const topic = getTopicFromAnonHostConfig(hostConfig);
	delete cachedTokens[topic];
	if (isBrowser()) {
		deleteFromLocalStorage(topic, ["access-token", "refresh-token"]);
		deleteFromSessionStorage(topic, ["access-token", "refresh-token"]);
	}
}

//#endregion
//#region src/auth/internal/default-auth-modules/oauth/oauth-utils.ts
function toPerformInteractiveLoginFunction(performInteractiveLogin) {
	if (typeof performInteractiveLogin === "string") {
		const fn = lookupInteractiveLoginFn(performInteractiveLogin);
		if (!fn) throw new Error(`No such function: ${performInteractiveLogin}`);
		return fn;
	}
	return performInteractiveLogin;
}
function lookupGetAccessFn(getAccessToken) {
	return globalThis[getAccessToken];
}
function lookupInteractiveLoginFn(name) {
	return globalThis[name];
}
function handlePossibleErrors(data) {
	if (data.errors) throw new AuthorizationError(data.errors);
}
function toQueryString(queryParams) {
	const queryParamsKeys = Object.keys(queryParams);
	queryParamsKeys.sort();
	return queryParamsKeys.map((k) => `${k}=${queryParams[k]}`).join("&");
}
function byteArrayToBase64(hashArray) {
	let result = "";
	if (isBrowser()) {
		const byteArrayToString = String.fromCharCode.apply(null, hashArray);
		result = btoa(byteArrayToString);
	} else if (isNode()) result = Buffer.from(hashArray).toString("base64");
	else throw new Error("Environment not supported for oauth2 authentication");
	return result;
}
/**
* @param message string to hash
* @returns sha-256 hashed string
*/
async function sha256(message) {
	const msgBuffer = new TextEncoder().encode(message);
	const hashBuffer = await globalThis.crypto.subtle.digest("SHA-256", msgBuffer);
	return byteArrayToBase64(Array.from(new Uint8Array(hashBuffer))).replaceAll(/\+/g, "-").replaceAll(/\//g, "_").replace(/=+$/, "");
}
async function createInteractiveLoginUrl(hostConfig, redirectUri, state, verifier) {
	const clientId = hostConfig.clientId || "";
	const locationUrl = toValidLocationUrl(hostConfig);
	const codeChallenge = await sha256(verifier);
	return `${locationUrl}/oauth/authorize?${toQueryString({
		response_type: "code",
		client_id: clientId,
		redirect_uri: redirectUri,
		scope: hostConfig.scope || "user_default",
		state,
		code_challenge: codeChallenge,
		code_challenge_method: "S256"
	})}`;
}
async function startFullPageLoginFlow(hostConfig) {
	const clientId = hostConfig.clientId || "";
	const locationUrl = toValidLocationUrl(hostConfig);
	const verifier = generateRandomString(128);
	const state = generateRandomString(43);
	const codeChallenge = await sha256(verifier);
	const redirectUri = hostConfig.redirectUri || globalThis.location.href;
	const topic = getTopicFromOauthHostConfig(hostConfig);
	clearStoredOauthTokens(hostConfig);
	saveInSessionStorage(topic, "state", state);
	saveInSessionStorage(topic, "verifier", verifier);
	saveInSessionStorage(topic, "href", globalThis.location.href);
	saveInSessionStorage("", "client-in-progress", topic);
	const url = `${locationUrl}/oauth/authorize?${toQueryString({
		response_type: "code",
		client_id: clientId,
		redirect_uri: redirectUri,
		scope: hostConfig.scope || "user_default",
		state,
		code_challenge: codeChallenge,
		code_challenge_method: "S256"
	})}`;
	globalThis.location.replace(url);
}
async function exchangeCodeAndVerifierForAccessTokenData(hostConfig, code, verifier, redirectUri) {
	try {
		const data = await (await fetch(`${toValidLocationUrl(hostConfig)}/oauth/token`, {
			method: "POST",
			credentials: "include",
			mode: "cors",
			headers: { "content-type": "application/json" },
			redirect: "follow",
			body: JSON.stringify({
				grant_type: "authorization_code",
				scope: hostConfig.scope || "user_default",
				...code ? { code } : {},
				redirect_uri: redirectUri || globalThis.location.href,
				...verifier ? { code_verifier: verifier } : {},
				client_id: hostConfig.clientId
			})
		})).json();
		handlePossibleErrors(data);
		return {
			accessToken: data.access_token,
			refreshToken: data.refresh_token,
			errors: data.errors
		};
	} catch (err) {
		console.error(err);
		return new Promise(() => {});
	}
}
function createBodyWithCredentialsEtc(clientId, clientSecret, scope, subject, userId) {
	const commonProps = {
		client_id: clientId,
		client_secret: clientSecret,
		scope
	};
	if (subject) return {
		...commonProps,
		grant_type: "urn:qlik:oauth:user-impersonation",
		user_lookup: {
			field: "subject",
			value: subject
		}
	};
	if (userId) return {
		...commonProps,
		grant_type: "urn:qlik:oauth:user-impersonation",
		user_lookup: {
			field: "userId",
			value: userId
		}
	};
	return {
		...commonProps,
		grant_type: "client_credentials"
	};
}
async function getOauthTokensWithCredentials(baseUrl, clientId, clientSecret, scope = "user_default", subject, userId) {
	const data = await (await fetch(`${baseUrl}/oauth/token`, {
		method: "POST",
		mode: "cors",
		headers: { "content-type": "application/json" },
		body: JSON.stringify(createBodyWithCredentialsEtc(clientId, clientSecret, scope, subject, userId))
	})).json();
	return {
		accessToken: data.access_token,
		refreshToken: data.refresh_token,
		errors: data.errors
	};
}
async function getOauthTokensWithRefreshToken(baseUrl, refreshToken, clientSecret) {
	const data = await (await fetch(`${baseUrl}/oauth/token`, {
		method: "POST",
		mode: "cors",
		headers: { "content-type": "application/json" },
		body: JSON.stringify({
			grant_type: "refresh_token",
			refresh_token: refreshToken,
			client_secret: clientSecret
		})
	})).json();
	return {
		accessToken: data.access_token,
		refreshToken: data.refresh_token,
		errors: data.errors
	};
}
async function getAnonymousOauthAccessToken(baseUrl, accessCode, clientId, trackingCode) {
	const data = await (await fetch(`${baseUrl}/oauth/token/anonymous-embed`, {
		method: "POST",
		mode: "cors",
		headers: { "content-type": "application/json" },
		body: JSON.stringify({
			eac: accessCode,
			client_id: clientId,
			grant_type: "urn:qlik:oauth:anonymous-embed",
			tracking_code: trackingCode
		})
	})).json();
	return {
		accessToken: data.access_token,
		refreshToken: data.refresh_token,
		errors: data.errors
	};
}
/**
* Fetches the access token from storage or memory.
* This code is intended to run in a node environment
*/
async function getOAuthTokensForNode(hostConfig) {
	const { clientId, clientSecret, performInteractiveLogin } = hostConfig;
	if (!clientId || !clientSecret && !performInteractiveLogin) throw new InvalidHostConfigError("A host config with authType set to \"oauth2\" has to provide a clientId and a clientSecret or a performInteractiveLogin function");
	return await loadOrAcquireAccessTokenOauth(hostConfig, async () => {
		if (hostConfig.performInteractiveLogin) return getOauthTokensWithInteractiveLogin(hostConfig);
		if (!hostConfig.clientId || !hostConfig.clientSecret) throw new InvalidHostConfigError("A host config with authType set to \"oauth2\" has to provide a clientId and a clientSecret");
		return getOauthTokensWithCredentials(toValidLocationUrl(hostConfig), hostConfig.clientId, hostConfig.clientSecret, hostConfig.scope, hostConfig.subject, hostConfig.userId);
	});
}
/**
* Fetches the access token from storage or memory. If no one is found a code and verifier is expected to be found in the sesion storage
* This code is intended to run in a browser
*/
async function getOAuthTokensForBrowser(hostConfig) {
	const { clientId } = hostConfig;
	if (!clientId) throw new InvalidHostConfigError("A host config with authType set to \"oauth2\" has to also provide a clientId");
	const oauthTokens = await loadOrAcquireAccessTokenOauth(hostConfig, async () => {
		if (hostConfig.getAccessToken) try {
			return {
				accessToken: typeof hostConfig.getAccessToken === "string" ? await lookupGetAccessFn(hostConfig.getAccessToken)() : await hostConfig.getAccessToken(),
				refreshToken: void 0,
				errors: void 0
			};
		} catch {
			return errorMessageToAuthData("Could not fetch access token using custom function");
		}
		if (hostConfig.performInteractiveLogin) return getOauthTokensWithInteractiveLogin(hostConfig);
		const topic = getTopicFromOauthHostConfig(hostConfig);
		const code = loadAndDeleteFromSessionStorage(topic, "code");
		const verifier = loadAndDeleteFromSessionStorage(topic, "verifier");
		if (code && verifier) {
			const tokenResponse = await exchangeCodeAndVerifierForAccessTokenData(hostConfig, code, verifier, hostConfig.redirectUri);
			if (tokenResponse) return tokenResponse;
		}
	});
	if (oauthTokens) return oauthTokens;
	if (hostConfig.performInteractiveLogin) return new Promise(() => {});
	await requestRedirect(hostConfig);
	startFullPageLoginFlow(hostConfig);
	return new Promise(() => {});
}
async function getOauthTokensWithInteractiveLogin(hostConfig) {
	if (!hostConfig.performInteractiveLogin) return errorMessageToAuthData("No performInteractiveLogin function in hostConfig");
	let usedRedirectUri;
	try {
		const verifier = generateRandomString(128);
		const originalState = generateRandomString(43);
		const { code, state } = extractCodeAndState(await toPerformInteractiveLoginFunction(hostConfig.performInteractiveLogin)({ getLoginUrl: async ({ redirectUri }) => {
			usedRedirectUri = redirectUri;
			return createInteractiveLoginUrl(hostConfig, redirectUri, originalState, verifier);
		} }));
		if (!usedRedirectUri) return errorMessageToAuthData("No redirect uri provided");
		if (originalState !== state) return errorMessageToAuthData("State returned by custom interactive login function does not match original");
		if (!code) return errorMessageToAuthData("No code found in response from custom interactive login function");
		return await exchangeCodeAndVerifierForAccessTokenData(hostConfig, code, verifier, usedRedirectUri);
	} catch (error) {
		return {
			accessToken: void 0,
			refreshToken: void 0,
			errors: [{
				code: "",
				status: 401,
				title: "Could not perform custom interactive login",
				detail: `${error}`
			}]
		};
	}
}
let lastOauthTokensCall = Promise.resolve("");
async function getOAuthAccessToken(hostConfig) {
	if (isNode()) {
		const tokens = await getOAuthTokensForNode(hostConfig);
		if (tokens) {
			handlePossibleErrors(tokens);
			return tokens.accessToken || "";
		}
		return "";
	}
	if (isBrowser()) lastOauthTokensCall = lastOauthTokensCall.then(async () => {
		const tokens = await getOAuthTokensForBrowser(hostConfig);
		if (tokens) {
			handlePossibleErrors(tokens);
			return tokens.accessToken || "";
		}
		return "";
	});
	return lastOauthTokensCall;
}
async function refreshAccessToken(hostConfig) {
	const tokens = await loadCachedOauthTokens(hostConfig);
	clearStoredOauthTokens(hostConfig);
	if (tokens && tokens.refreshToken && hostConfig.clientSecret) {
		const refreshedTokens = await loadOrAcquireAccessTokenOauth(hostConfig, async () => {
			if (!tokens || !tokens.refreshToken || !hostConfig.clientSecret) throw new Error("Trying to refresh tokens without refreshToken or clientSecret");
			return getOauthTokensWithRefreshToken(toValidLocationUrl(hostConfig), tokens.refreshToken, hostConfig.clientSecret);
		});
		if (refreshedTokens) handlePossibleErrors(refreshedTokens);
	}
}
function extractCodeAndState(input) {
	if (typeof input === "string") {
		let parsedUrl;
		try {
			parsedUrl = new URL(input);
		} catch {
			parsedUrl = new URL(input, "http://localhost");
		}
		const queryParams = new URLSearchParams(parsedUrl.search);
		return {
			code: queryParams.get("code") || "",
			state: queryParams.get("state") || ""
		};
	}
	return input;
}
function errorMessageToAuthData(message) {
	return {
		accessToken: void 0,
		refreshToken: void 0,
		errors: [{
			code: "",
			status: 401,
			title: message,
			detail: ""
		}]
	};
}

//#endregion
//#region src/auth/internal/default-auth-modules/oauth/temporary-token.ts
async function exchangeAccessTokenForTemporaryToken(hostConfig, accessToken, purpose) {
	const response = await fetch(`${toValidLocationUrl(hostConfig)}/oauth/token`, {
		method: "POST",
		credentials: "include",
		mode: "cors",
		headers: { "content-type": "application/json" },
		redirect: "follow",
		body: JSON.stringify({
			subject_token: accessToken,
			subject_token_type: "urn:ietf:params:oauth:token-type:access_token",
			grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
			purpose,
			redirect_uri: globalThis.location?.href,
			client_id: hostConfig.clientId
		})
	});
	if (response.status !== 200) throw await toError(response);
	return (await response.json()).access_token;
}
async function toError(response) {
	const body = await response.text();
	try {
		return new AuthorizationError(JSON.parse(body).errors);
	} catch {
		return new AuthorizationError([{
			code: "unknown",
			status: response.status,
			detail: body,
			title: "Unknown authentication error"
		}]);
	}
}

//#endregion
//#region src/auth/internal/default-auth-modules/anonymous.ts
/**
* Retries the passed in function after calling handleAuthenticationError
*/
async function handlePotentialAuthenticationErrorAndRetry$1(hostConfig, fn) {
	try {
		return await fn();
	} catch (err) {
		const { retry } = await handleAuthenticationError$9({
			hostConfig,
			canRetry: true
		});
		if (retry) return fn();
		throw err;
	}
}
async function getOrCreateTrackingCode(hostConfig) {
	let trackingCode;
	if (isBrowser()) {
		const topic = getTopicFromAnonHostConfig(hostConfig);
		trackingCode = loadFromLocalStorage(topic, "tracking-code");
		if (!trackingCode) trackingCode = createTrackingCode();
		saveInLocalStorage(topic, "tracking-code", trackingCode);
	} else trackingCode = createTrackingCode();
	return trackingCode;
}
function createTrackingCode() {
	const timeStamp = Math.floor(Date.now() / 1e3).toString(16);
	return `${timeStamp}${generateRandomHexString(40 - timeStamp.length)}`;
}
async function getAnonymousAccessToken(hostConfig) {
	const { accessCode, clientId } = hostConfig;
	if (!accessCode || !clientId) throw new InvalidHostConfigError("A host config with authType set to \"anonymous\" has to provide both an accessCode and clientId");
	const tokens = await loadOrAcquireAccessTokenAnon(hostConfig, async () => {
		return getAnonymousOauthAccessToken(toValidLocationUrl(hostConfig), accessCode, clientId, await getOrCreateTrackingCode(hostConfig));
	});
	if (!tokens) return "";
	if (tokens.errors) throw new AuthorizationError(tokens.errors);
	if (tokens.accessToken) return tokens.accessToken;
	return "";
}
async function getRestCallAuthParams$9({ hostConfig }) {
	return {
		headers: { Authorization: `Bearer ${await getAnonymousAccessToken(hostConfig)}` },
		queryParams: {},
		credentials: "omit"
	};
}
async function getWebSocketAuthParams$9({ hostConfig }) {
	if (isNode()) return { headers: { Authorization: `Bearer ${await getAnonymousAccessToken(hostConfig)}` } };
	return { queryParams: { accessToken: await handlePotentialAuthenticationErrorAndRetry$1(hostConfig, async () => {
		return exchangeAccessTokenForTemporaryToken(hostConfig, await getAnonymousAccessToken(hostConfig), "websocket");
	}) } };
}
async function getWebResourceAuthParams$2({ hostConfig }) {
	return { queryParams: { accessToken: await handlePotentialAuthenticationErrorAndRetry$1(hostConfig, async () => {
		return exchangeAccessTokenForTemporaryToken(hostConfig, await getAnonymousAccessToken(hostConfig), "websocket");
	}) } };
}
async function handleAuthenticationError$9({ hostConfig }) {
	clearStoredAnonymousTokens(hostConfig);
	return {
		preventDefault: false,
		retry: true
	};
}
var anonymous_default = {
	requiredProps: ["clientId", "accessCode"],
	optionalProps: [],
	getRestCallAuthParams: getRestCallAuthParams$9,
	getWebSocketAuthParams: getWebSocketAuthParams$9,
	getWebResourceAuthParams: getWebResourceAuthParams$2,
	handleAuthenticationError: handleAuthenticationError$9
};

//#endregion
//#region src/auth/internal/default-auth-modules/apikey.ts
function getRestCallAuthParams$8({ hostConfig }) {
	return Promise.resolve({
		headers: { Authorization: `Bearer ${hostConfig?.apiKey}` },
		queryParams: {},
		credentials: "omit"
	});
}
async function getWebSocketAuthParams$8({ hostConfig }) {
	if (isBrowser()) throw new Error("Not supported in browser environment");
	return Promise.resolve({ headers: { Authorization: `Bearer ${hostConfig?.apiKey}` } });
}
function handleAuthenticationError$8() {
	return Promise.resolve({});
}
var apikey_default = {
	requiredProps: ["apiKey"],
	optionalProps: [],
	getRestCallAuthParams: getRestCallAuthParams$8,
	getWebSocketAuthParams: getWebSocketAuthParams$8,
	handleAuthenticationError: handleAuthenticationError$8
};

//#endregion
//#region src/invoke-fetch/invoke-fetch-errors.ts
/**
* Error thrown by the invokeFetch function
*/
var InvokeFetchError = class extends Error {
	status;
	headers;
	data;
	constructor(errorMessage, status, headers, data) {
		super(errorMessage);
		this.status = status;
		this.headers = headers;
		this.data = data;
		this.stack = cleanStack(this.stack);
	}
};
/**
* Error thrown if a request-body cannot be encoded.
*/
var EncodingError = class extends Error {
	contentType;
	data;
	constructor(errorMessage, contentType, data) {
		super(errorMessage);
		this.contentType = contentType;
		this.data = data;
		this.stack = cleanStack(this.stack);
	}
};
const regex = /^.+\/qmfe-api(?:\.js)?:(\d+)(?::\d+)?$/gim;
const isFromQmfeApi = (line) => {
	const matches = line.match(regex);
	return Boolean(matches && matches.length > 0);
};
/**
* Function that removes stack entries that are from the qmfe-api module
*/
function cleanStack(stack) {
	if (!stack) return stack;
	const newStack = [];
	const lines = stack.split("\n");
	lines.reverse();
	for (const line of lines) {
		if (isFromQmfeApi(line)) break;
		newStack.unshift(line);
	}
	return newStack.join("\n");
}

//#endregion
//#region src/invoke-fetch/internal/invoke-fetch-helpers.ts
/**
* Appends the specified query parameters (encoded) to the provided url.
* @private
*/
function appendQueryToUrl(url, queryParams) {
	const queryString = encodeQueryParams(queryParams);
	let firstQueryDelim;
	if (url.indexOf("?") === -1) firstQueryDelim = "?";
	else firstQueryDelim = "&";
	return `${url}${firstQueryDelim}${queryString}`;
}
/**
* Encode the query map into a query string
* @private
*/
function encodeQueryParams(query) {
	if (!query) return "";
	return Object.entries(query).map((kv) => {
		const [, value] = kv;
		if (value === void 0) return;
		return kv.map((val) => encodeURIComponent(decodeURIComponent(val))).join("=");
	}).filter(Boolean).join("&");
}
/**
* Replaces the variable placeholders in the path template with the variable values
* @param pathTemplate
* @param pathVariables
* @private
*/
function applyPathVariables(pathTemplate, pathVariables) {
	let result = pathTemplate;
	if (pathVariables) Object.keys(pathVariables).forEach((key) => {
		result = result.replace(`{${key}}`, pathVariables[key]);
	});
	return result;
}
/**
* Join url and query to a complete url
* @private
*/
function toCompleteUrl(url, query) {
	if (query !== "") return `${url}?${query}`;
	return url;
}
/**
* Returns true if the status code is 301 or 302 or for ..../qps/csrftoken with 403 which really should be 401 on windows but isn't for historical reasons
*/
function means401OnWindows(completeUrl, statusCode) {
	const url = new URL(completeUrl, "http://dummy");
	const isQpsCsrfTokenUrl = url.pathname.endsWith("/qps/csrftoken") && url.searchParams.has("xrfkey");
	return statusCode === 301 || statusCode === 302 || isQpsCsrfTokenUrl && statusCode === 403;
}

//#endregion
//#region src/invoke-fetch/internal/invoke-xhr.ts
async function invokeXHR(completeUrl, { method, headers, credentials, keepalive, body, signal, progress }) {
	const xhr = new XMLHttpRequest();
	let resolve;
	let reject;
	const promise = new Promise((res, rej) => {
		resolve = res;
		reject = rej;
	});
	xhr.open(method || "GET", completeUrl);
	if (typeof headers === "object") for (const [key, value] of Object.entries(headers)) xhr.setRequestHeader(key, value);
	else throw Error("malformed headers", headers);
	if (keepalive) xhr.setRequestHeader("Connection", "keep-alive");
	if (signal) signal.addEventListener("abort", () => {
		xhr.abort();
		reject();
	});
	if (credentials === "include") xhr.withCredentials = true;
	else xhr.withCredentials = false;
	if (progress?.onUpload) xhr.upload.onprogress = (event) => {
		const { loaded, total, lengthComputable } = event;
		progress.onUpload?.({
			loaded,
			total: lengthComputable ? total : void 0
		});
	};
	if (progress?.onDownload) xhr.onprogress = (event) => {
		const { loaded, total, lengthComputable } = event;
		progress.onDownload?.({
			loaded,
			total: lengthComputable ? total : void 0
		});
	};
	xhr.onloadend = () => {
		const { status } = xhr;
		const responseHeaders = {};
		for (const line of xhr.getAllResponseHeaders().split("\r\n")) {
			const [key, value] = line.split(":", 2);
			if (key && value) responseHeaders[key] = value;
		}
		if (xhr.response) resolve(new Response(xhr.response, {
			status,
			headers: responseHeaders
		}));
		else resolve(new Response(void 0, {
			status,
			headers: responseHeaders
		}));
	};
	try {
		const bod = body;
		xhr.send(bod);
	} catch (e) {
		return Promise.reject(new InvokeFetchError(getErrorMessage(e), 0, new Headers(), {}));
	}
	return promise;
}

//#endregion
//#region src/invoke-fetch/internal/response-cache.ts
const responseCaches = {};
globalThis.__API_CACHE__DO_NOT_USE_OR_YOU_WILL_BE_FIRED = responseCaches;
if (globalThis.QlikMain?.INTERNAL__DO_NOT_USE) globalThis.QlikMain.INTERNAL__DO_NOT_USE.apiCache = responseCaches;
let defaultCacheTime = 1e3 * 60 * 10;
/**
* The global namespace is used as a fallback if an entry is not found in a specific cache.
* This cache can be used internally, specifically by BFF interceptors,
* where we cannot reliably map a response to an API-module.
*/
const globalCacheNamespace = ".global";
/**
* Gets a cached response-promise from the specified API-namespace (or global cache).
* A promise is returned if options.noCache is not true, the promise exists and is not too
* old (based on defaults and cache-options).
*
* @param api The cache-namespace of the API.
* @param props CachingContext
* @returns The cached response (cloned and ready to use) or undefined.
*/
function getFromCache(api, props) {
	if (isForbiddenApiName(api)) throw new Error(`Forbidden api name: ${api}`);
	if (props?.options?.noCache) return;
	const { method, completeUrl, cacheKey } = props;
	const caches = getPossibleCaches(api);
	if (isModifyingOperation(method, completeUrl)) return;
	let entry;
	for (const cache of caches) if (cacheKey in cache) {
		entry = cache[cacheKey];
		if (entry && typeof entry.value !== "undefined" && shouldUseCachedResult(props?.options, entry, defaultCacheTime)) break;
		entry = void 0;
	}
	if (!entry) return;
	entry.lastHit = /* @__PURE__ */ new Date();
	entry.hitCount += 1;
	entry.accessedPaths.add(globalThis.location ? globalThis.location.pathname : "node");
	const value = entry.value;
	return cloneResultPromise(value);
}
/**
* Updates the cache of 'api' with the response-promise based on the caching-context.
* If the promise comes from a modifying operation, related cache entries will be removed.
* The returned promise is either the original promise (if it isn't cached) or a
* cloned promise that is safe to use.
*
* Note: The original response-promise should not be reused directly, use the returned
* equivalent instead.
*
* @private
* @param api The cache-namespace of the API.
* @param props CachingContext
* @param responsePromise Promise<InvokeFetchResponse>, the thing to cache
* @returns
*/
function updateCache(api, props, responsePromise) {
	if (isForbiddenApiName(api)) throw new Error(`Forbidden api name: ${api}`);
	if (!responseCaches[api]) responseCaches[api] = {};
	const { cacheKey } = props;
	if (isModifyingOperation(props.method, props.completeUrl)) return responsePromise.then((res) => {
		const caches = getPossibleCaches(api);
		for (const cache of caches) clearRelatedCacheEntries(cache, cacheKey);
		return res;
	});
	const cacheNamespace = responseCaches[api];
	const responsePromiseWithCacheClearing = responsePromise.catch((error) => {
		delete cacheNamespace[cacheKey];
		return Promise.reject(error);
	});
	cacheNamespace[cacheKey] = {
		lastPulled: Date.now(),
		value: responsePromiseWithCacheClearing,
		lastHit: null,
		hitCount: 0,
		accessedPaths: /* @__PURE__ */ new Set()
	};
	return cloneResultPromise(responsePromiseWithCacheClearing);
}
/**
* Returns true if it is ok the to use the cached promise from a previous invocation
* @private
*/
function shouldUseCachedResult(options, cacheEntry, defaultMaxCacheTime) {
	if (options?.noCache) return false;
	if (!cacheEntry || typeof cacheEntry.value === "undefined") return false;
	if (options?.useCacheIfAfter) return options.useCacheIfAfter.getTime() <= cacheEntry.lastPulled;
	const age = Date.now() - cacheEntry.lastPulled;
	if (typeof options?.maxCacheAge === "number") return age <= options?.maxCacheAge;
	return age < defaultMaxCacheTime;
}
/**
* Builds a key used for caching based on a CachingContext
* @private
* @param cachingContext
* @returns a key used for caching
*/
function toCacheKey({ url, query, headers, serializedHostConfig }) {
	let cacheKey = url;
	if (query && Object.keys(query).length > 0) {
		const queryString = encodeQueryParams(query);
		if (url.includes("?")) cacheKey = cacheKey.concat(`&${queryString}`);
		else cacheKey = cacheKey.concat(`?${queryString}`);
	}
	if (headers && Object.keys(headers).length > 0) cacheKey = cacheKey.concat(`+headers=${JSON.stringify(headers)}`);
	if (serializedHostConfig && serializedHostConfig !== "{}") cacheKey = cacheKey.concat(`+host-config=${serializedHostConfig}`);
	return cacheKey;
}
/**
* Convenience function for getting a list of caches that are OK to use
* in the context of an API-namespace.
* @param api
* @returns
*/
function getPossibleCaches(api) {
	const caches = [];
	if (responseCaches[api]) caches.push(responseCaches[api]);
	if (responseCaches[".global"]) caches.push(responseCaches[globalCacheNamespace]);
	return caches;
}
/**
* @private
* Clears all cache entries where the modifying URL starts with the cached URL
* @param cache
* @param cacheKey
*/
function clearRelatedCacheEntries(cache, cacheKey) {
	const modifyingUrl = cacheKeyToUrl(cacheKey);
	for (const existingCacheKey in cache) {
		const cleanUrl = cacheKeyToUrl(existingCacheKey);
		if (modifyingUrl.startsWith(cleanUrl) || cleanUrl.startsWith(modifyingUrl)) delete cache[existingCacheKey];
	}
}
/**
* Removes query, headers and host-config from the cache key to get a clean URL.
*/
function cacheKeyToUrl(cachedUrl) {
	const queryIdx = cachedUrl.indexOf("?");
	if (queryIdx >= 0) return cachedUrl.substring(0, queryIdx);
	const headersIdx = cachedUrl.indexOf("+headers=");
	if (headersIdx >= 0) return cachedUrl.substring(0, headersIdx);
	const hostConfigIdx = cachedUrl.indexOf("+host-config=");
	if (hostConfigIdx >= 0) return cachedUrl.substring(0, hostConfigIdx);
	return cachedUrl;
}
/**
* Clone mutable-objects using JSON.stringify.
* Strings, Blobs and ReadableStreams are not cloned.
* @private
*/
function clone(value) {
	if (typeof value === "undefined" || value === null) return value;
	if (value && (value instanceof Blob || value instanceof Object && value.toString() === "[object Blob]")) return value;
	if (value && value instanceof ReadableStream) return value;
	if (typeof value === "string") return value;
	return JSON.parse(JSON.stringify(value));
}
/**
* Clones the result so that no one can tamper with the cache. Note that the headers object is NOT cloned
* @private
*/
function cloneResultPromise(value) {
	return value.then((resp) => {
		const result = {
			data: clone(resp.data),
			headers: resp.headers,
			status: resp.status
		};
		if (resp.next) result.next = resp.next;
		if (resp.prev) result.prev = resp.prev;
		return result;
	});
}
/**
* Clears cached responses
* @param api the api to clear cache in
*/
function clearApiCacheInternal(api) {
	responseCaches[api] = {};
}
/**
* Returns true if the method might modify things requiring caches to be cleared
*/
function isModifyingOperation(method, url) {
	if (method === "get" || method === "GET") return false;
	if (url.endsWith("/api/v1/licenses/allotments") && (method === "post" || method === "POST")) return false;
	return true;
}
function isForbiddenApiName(api) {
	return api === "__proto__";
}

//#endregion
//#region src/invoke-fetch/internal/invoke-fetch-methods.ts
function getErrorMessage(error) {
	if (error instanceof Error) {
		if (error.cause) {
			if (error.message) return `${error.message} - ${getErrorMessage(error.cause)}`;
			return getErrorMessage(error.cause);
		}
		return error.message;
	}
	return String(error);
}
async function fetchAndTransformExceptions(input, init) {
	try {
		return await fetch(input, init);
	} catch (e) {
		return Promise.reject(new InvokeFetchError(getErrorMessage(e), 0, new Headers(), {}));
	}
}
async function performActualHttpFetch(method, completeUrl, unencodedBody, contentType, options, authHeaders, credentials, userAgent) {
	const { body, contentTypeHeader, requestOptions } = encodeBody(unencodedBody, contentType ?? "");
	const headers = {
		...contentTypeHeader,
		...authHeaders,
		...options?.headers,
		...getServiceOverrideHeaderFromLocalStorage()
	};
	if (!headers["User-Agent"] && userAgent) headers["User-Agent"] = userAgent;
	const request = {
		method,
		credentials,
		mode: "cors",
		headers,
		redirect: await isWindows(options?.hostConfig) ? "manual" : "follow",
		keepalive: options?.keepalive,
		progress: options?.progress,
		body,
		...requestOptions
	};
	let fetchTimeoutId;
	if (options?.signal) request.signal = options.signal;
	else if (options?.timeoutMs && options.timeoutMs > 0) {
		const controller = new AbortController();
		fetchTimeoutId = setTimeout(() => {
			controller.abort();
		}, options.timeoutMs);
		request.signal = controller.signal;
	}
	let response;
	if (options?.progress) response = await invokeXHR(completeUrl, request);
	else response = await fetchAndTransformExceptions(completeUrl, request);
	if (fetchTimeoutId) clearTimeout(fetchTimeoutId);
	return await parseFetchResponse(response, completeUrl);
}
function encodeBody(unencodedBody, contentType) {
	if (!unencodedBody) return {
		body: null,
		contentTypeHeader: {},
		requestOptions: {}
	};
	const contentTypeHeader = {};
	const requestOptions = {};
	let body = null;
	switch (contentType) {
		case "":
		case "application/json":
			contentTypeHeader["Content-Type"] = "application/json";
			body = JSON.stringify(unencodedBody);
			break;
		case "multipart/form-data":
			body = encodeMultipartBody(unencodedBody);
			break;
		case "application/octet-stream":
			contentTypeHeader["Content-Type"] = contentType;
			requestOptions["duplex"] = "half";
			body = unencodedBody;
			break;
		case "text/plain":
			if (typeof unencodedBody === "string") {
				contentTypeHeader["Content-Type"] = contentType;
				body = unencodedBody;
			} else throw new EncodingError(`Cannot send ${typeof unencodedBody} as ${contentType}, body should be a string.`, contentType, unencodedBody);
			break;
		default: throw new EncodingError(`Unsupported content-type "${contentType}", supported are: application/json, multipart/form-data, application/octet-stream and text/plain`, contentType, unencodedBody);
	}
	return {
		body,
		contentTypeHeader,
		requestOptions
	};
}
function encodeMultipartBody(unencodedBody) {
	const contentType = "multipart/form-data";
	if (typeof unencodedBody !== "object") throw new EncodingError(`Cannot encode ${typeof unencodedBody} as ${contentType}, body should be an object.`, contentType, unencodedBody);
	if (Array.isArray(unencodedBody)) throw new EncodingError(`Cannot encode ${typeof unencodedBody} as ${contentType}, body should be an object.`, contentType, unencodedBody);
	if (unencodedBody instanceof FormData) return unencodedBody;
	const form = new FormData();
	Object.entries(unencodedBody).forEach((entry) => {
		const [key, value] = entry;
		switch (typeof value) {
			case "boolean":
			case "number":
			case "string":
				form.set(key, `${value}`);
				break;
			case "object":
				if (value instanceof Blob) form.set(key, value);
				else if (value instanceof Uint8Array) {
					const data = new Blob([value], { type: "application/octet-stream" });
					form.set(key, data);
				} else {
					const json = JSON.stringify(value);
					const data = new Blob([json], { type: "application/json" });
					form.set(key, data, "");
				}
				break;
			default: throw new EncodingError(`Cannot encode multipart-field "${key}" with value of type ${typeof value}, values must be objects, strings, numbers or boolean.`, contentType, unencodedBody);
		}
	});
	return form;
}
async function getInvokeFetchUrlParams({ method, pathTemplate, pathVariables, query, options }) {
	const locationUrl = toValidLocationUrl(options?.hostConfig);
	const { headers: authHeaders, queryParams: authQueryParams, credentials } = await getRestCallAuthParams({
		hostConfig: options?.hostConfig,
		method
	});
	const url = locationUrl + applyPathVariables(pathTemplate, pathVariables);
	const completeUrl = toCompleteUrl(url, encodeQueryParams({
		...query,
		...authQueryParams
	}));
	const serializedHostConfig = serializeHostConfig(options?.hostConfig);
	return {
		completeUrl,
		authHeaders,
		credentials,
		cacheKey: toCacheKey({
			url,
			query,
			headers: options?.headers,
			serializedHostConfig
		})
	};
}
function invokeFetchWithUrl(api, props) {
	return invokeFetchWithUrlAndRetry(api, props, async () => {
		const { authHeaders, credentials } = await getInvokeFetchUrlParams(props);
		return invokeFetchWithUrlAndRetry(api, {
			...props,
			authHeaders,
			credentials,
			options: {
				...props.options,
				noCache: true
			}
		}, void 0);
	});
}
function invokeFetchWithUrlAndRetry(api, props, performRetry) {
	const { method, completeUrl, body, options, authHeaders, credentials, cacheKey, contentType, userAgent } = props;
	const cachingContext = {
		method,
		completeUrl,
		cacheKey,
		options
	};
	const cachedResponse = getFromCache(api, cachingContext);
	if (cachedResponse) return cachedResponse;
	const resultPromiseFromBackend = performActualHttpFetch(method, completeUrl, body, contentType, options, authHeaders, credentials, userAgent);
	const resultAfterAuthenticationCheck = interceptAuthenticationErrors(options?.hostConfig, completeUrl, resultPromiseFromBackend, performRetry);
	return updateCache(api, cachingContext, addPagingFunctions(api, {
		...props,
		value: resultAfterAuthenticationCheck
	}));
}
/**
* Adds paging functions to the response object if there are paging links present in the response body
*/
function addPagingFunctions(api, { method, body, options, authHeaders, credentials, value }) {
	return value.then((resp) => {
		const dataWithPotentialLinks = resp.data;
		if (!dataWithPotentialLinks) return resp;
		const serializedHostConfig = serializeHostConfig(options?.hostConfig);
		const prevUrl = dataWithPotentialLinks.links?.prev?.href;
		const nextUrl = dataWithPotentialLinks.links?.next?.href;
		if (prevUrl) resp.prev = (prevOptions) => invokeFetchWithUrl(api, {
			method,
			completeUrl: prevUrl,
			body,
			options: prevOptions || options,
			authHeaders,
			credentials,
			cacheKey: toCacheKey({
				url: prevUrl,
				headers: options?.headers,
				serializedHostConfig
			})
		});
		if (nextUrl) resp.next = (nextOptions) => invokeFetchWithUrl(api, {
			method,
			completeUrl: nextUrl,
			body,
			options: nextOptions || options,
			authHeaders,
			credentials,
			cacheKey: toCacheKey({
				url: nextUrl,
				headers: options?.headers,
				serializedHostConfig
			})
		});
		return resp;
	});
}
function neverResolvingPromise() {
	return new Promise(() => {});
}
async function interceptAuthenticationErrors(hostConfig, completeUrl, resultPromise, performRetry) {
	try {
		return await resultPromise;
	} catch (error) {
		const err = error;
		const errorBody = err.data;
		if (err.status === 401 || err.status === 403 && errorBody?.code === "CSRF-TOKEN-2" || means401OnWindows(completeUrl, err.status) && await isWindows(hostConfig)) {
			if (globalThis.loggingOut) return neverResolvingPromise();
			const { retry, preventDefault } = await handleAuthenticationError({
				hostConfig,
				status: err.status,
				headers: err.headers,
				errorBody,
				canRetry: !!performRetry
			});
			if (retry && performRetry) return performRetry();
			if (preventDefault) return neverResolvingPromise();
		}
		throw error;
	}
}
/** Set the X-Qlik-Overrides header according to what's written to localStorage
*  by qmfe-devtool */
function getServiceOverrideHeaderFromLocalStorage() {
	if (!isBrowser()) return {};
	const header = localStorage.getItem("qmfe-api-service-overrides-header");
	if (!header) return {};
	return { "X-Qlik-Overrides": header };
}
/** Convert a Blob to a DownloadableBlob */
function toDownloadableBlob(blob, name) {
	const result = blob;
	if (name) result.download = (filename = name) => download(blob, filename);
	else result.download = (filename) => download(blob, filename);
	return result;
}
/** Convenience-function for downloading a blob. */
async function download(blob, filename) {
	if (isBrowser()) {
		const a = document.createElement("a");
		const blobUrl = window.URL.createObjectURL(blob);
		a.href = blobUrl;
		a.download = filename;
		a.click();
		window.URL.revokeObjectURL(blobUrl);
	} else {
		const { writeFileSync } = await import("fs");
		const arrayBuffer = await blob.arrayBuffer();
		writeFileSync(filename, new Uint8Array(arrayBuffer));
	}
}

//#endregion
//#region src/invoke-fetch/invoke-fetch-functions.ts
const defaultUserAgent = "qmfe-api/latest";
/**
* Makes an HTTP call with the browser's fetch API to the endpoint configured in the call's arguments
* @param api Name of api to call. Will be used for caching responses.
* @param invokeFetchProps InvokeFetchProperties
*/
async function invokeFetch(api, props, interceptors) {
	const effectiveInterceptors = interceptors || getInterceptors();
	const invokeFetchFinal = (reqeust) => invokeFetchIntercepted(api, reqeust);
	return (effectiveInterceptors || []).reduce((proceed, interceptor) => (request) => interceptor(request, proceed), invokeFetchFinal)(props);
}
/** @private Runs the intercepted version of invoke fetch */
async function invokeFetchIntercepted(api, props) {
	if (props.options?.hostConfig?.authType === "mock-backend-rest-recorder") return Promise.resolve(props.options?.hostConfig.recordInvokeFetch(props));
	if (props.options?.hostConfig?.authType === "mock-backend") return Promise.resolve(props.options?.hostConfig.mockInvokeFetch(props));
	checkForCrossDomainRequest(props.options?.hostConfig);
	if (props.options?.hostConfig?.authType === "noauth") {
		console.info("API call blocked: noauth auth module is configured, no API calls should be made");
		return {
			status: 204,
			headers: new Headers(),
			data: null
		};
	}
	let userAgent;
	if (props?.userAgent) userAgent = props.userAgent;
	else if (isBrowser()) userAgent = `${window.navigator.userAgent} ${defaultUserAgent}`;
	else userAgent = defaultUserAgent;
	const { completeUrl, authHeaders, credentials, cacheKey } = await getInvokeFetchUrlParams(props);
	return invokeFetchWithUrl(api, {
		...props,
		method: props.method.toUpperCase(),
		completeUrl,
		authHeaders,
		credentials,
		cacheKey,
		userAgent
	});
}
/**
* Clears cached responses for a certain api
* @example
* clearNameSpaceCache("spaces"); // clears the cache for the "spaces" api
* @param api the api namespace to clear cache in
*/
function clearApiCache(api) {
	clearApiCacheInternal(api);
}
/**
* Function that extracts the payload from the fetch response.
* @param fetchResponse fetch response
* @param url to where the request as made
* @returns parsed fetch response
*/
async function parseFetchResponse(fetchResponse, url) {
	let resultData;
	const contentType = fetchResponse.headers.get("content-type")?.split(";")[0];
	const contentDisposition = fetchResponse.headers.get("content-disposition")?.split(";");
	if (contentDisposition && contentDisposition[0] === "attachment") {
		let filename = "";
		for (let i = 1; i < contentDisposition.length; i++) {
			const attr = contentDisposition[i].trim();
			if (attr.indexOf("filename") === 0) {
				const start = attr.indexOf("\"");
				const end = attr.lastIndexOf("\"");
				filename = attr.slice(start + 1, end);
			}
		}
		resultData = toDownloadableBlob(await fetchResponse.blob(), filename);
	} else switch (contentType) {
		case "image/png":
		case "image/jpeg":
		case "image/x-icon":
		case "application/offset+octet-stream":
		case "application/octet-stream":
		case "application/zip":
		case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
		case "application/pdf":
			resultData = toDownloadableBlob(await fetchResponse.blob());
			break;
		case "text/event-stream":
			resultData = fetchResponse.body;
			break;
		default:
			try {
				resultData = await fetchResponse.text();
				resultData = JSON.parse(resultData);
			} catch {}
			break;
	}
	const { status, statusText, headers } = fetchResponse;
	const errorMsg = `request to '${url}' failed with status ${status} ${statusText}.`;
	if (status >= 300) throw new InvokeFetchError(errorMsg, status, headers, resultData);
	if (status === 0) throw new InvokeFetchError(errorMsg, 302, headers, resultData);
	return {
		status,
		headers,
		data: resultData
	};
}

//#endregion
//#region src/http/http-constants.ts
/**
* Name of the CSRF Token header.
*/
const QLIK_CSRF_TOKEN = "qlik-csrf-token";

//#endregion
//#region src/http/http-functions.ts
/**
* Clears the CSRF token
* @param location an optional location that is used to clear the csrf tokens when embedded
*/
function clearCsrfToken(hostConfig) {
	const locationUrl = toValidLocationUrl(hostConfig);
	delete csrfTokens[locationUrl];
	clearApiCache("csrf-token");
}
/**
* Fetches the CSRF token from the server. The token is cached and will be reused for subsequent calls.
* @param hostConfig HostConfig object containing the location url and authentication options
* @param noCache Set to true to force a new token to be fetched
* @returns csrf token
*/
async function getCsrfToken(hostConfig, noCache) {
	const locationUrl = toValidLocationUrl(hostConfig);
	let pathTemplate;
	if (await isWindows(hostConfig)) pathTemplate = "/qps/csrftoken";
	else pathTemplate = "/api/v1/csrf-token";
	const fetchCsrfToken = async () => {
		try {
			const csrfToken = (await invokeFetch("csrf-token", {
				method: "get",
				pathTemplate,
				options: {
					hostConfig,
					maxCacheAge: 5e3,
					noCache
				}
			})).headers.get(QLIK_CSRF_TOKEN);
			if (!csrfToken) return "";
			return csrfToken;
		} catch (e) {
			if (e.status === 404) return "";
			throw e;
		}
	};
	if (noCache) {
		csrfTokens[locationUrl] = fetchCsrfToken();
		return csrfTokens[locationUrl];
	}
	csrfTokens[locationUrl] = csrfTokens[locationUrl] || fetchCsrfToken();
	return csrfTokens[locationUrl];
}
const csrfTokens = {};

//#endregion
//#region src/auth/internal/internal-auth-functions.ts
/**
* Returns the fetch credentials string based on the host config. Note that this
* assumes cookies are used so it should only be used by the cookie auth modes
* @param hostConfig the HostConfig containing authentication details
* @private
*/
function internalGetCredentialsForCookieAuth(hostConfig) {
	if (hostConfig.crossSiteCookies === false) return "same-origin";
	if (isHostCrossOrigin(hostConfig)) return "include";
	return "same-origin";
}
async function resolveTokenIfPresent(getAccessToken) {
	if (typeof getAccessToken === "function") return getAccessToken();
	if (typeof getAccessToken === "string") {
		const accessTokenFn = lookupGlobalGetAccessTokenFn(getAccessToken);
		if (typeof accessTokenFn !== "function") throw new Error(`getAccessToken function "${getAccessToken}" not found on globalThis. Please make sure it is defined.`);
		return accessTokenFn();
	}
}
function lookupGlobalGetAccessTokenFn(getAccessToken) {
	return globalThis[getAccessToken];
}

//#endregion
//#region src/auth/internal/default-auth-modules/cookie.ts
/**
* Returns true if the verb might modify things requiring caches to be cleared
*/
function isModifyingVerb(verb) {
	return !(verb === "get" || verb === "GET");
}
async function getRestCallAuthParams$7({ hostConfig, method }) {
	const headers = {};
	if (isModifyingVerb(method)) headers["qlik-csrf-token"] = await getCsrfToken(hostConfig);
	if (hostConfig.webIntegrationId) headers["qlik-web-integration-id"] = hostConfig.webIntegrationId;
	return {
		headers,
		queryParams: {},
		credentials: internalGetCredentialsForCookieAuth(hostConfig)
	};
}
async function getWebSocketAuthParams$7({ hostConfig }) {
	if (isNode()) {
		const headers = {};
		headers["qlik-csrf-token"] = await getCsrfToken(hostConfig);
		if (hostConfig.webIntegrationId) headers["qlik-web-integration-id"] = hostConfig.webIntegrationId;
		return { headers };
	}
	const params = { "qlik-csrf-token": await getCsrfToken(hostConfig, true) };
	if (hostConfig.webIntegrationId) params["qlik-web-integration-id"] = hostConfig.webIntegrationId;
	return { queryParams: params };
}
async function handleAuthenticationError$7({ hostConfig, status }) {
	clearCsrfToken(hostConfig);
	if (status === 403) return {
		preventDefault: false,
		retry: true
	};
	const webIntegrationParam = hostConfig.webIntegrationId ? `qlik-web-integration-id=${hostConfig?.webIntegrationId}&` : "";
	const locationUrl = toValidLocationUrl(hostConfig);
	await requestRedirect(hostConfig);
	globalThis.location.replace(`${locationUrl}/login?${webIntegrationParam}returnto=${encodeURIComponent(globalThis.location.href)}`);
	return { preventDefault: true };
}
/** @private */
var cookie_default = {
	requiredProps: [],
	optionalProps: [
		"webIntegrationId",
		"crossSiteCookies",
		"anonymousMode"
	],
	getRestCallAuthParams: getRestCallAuthParams$7,
	getWebSocketAuthParams: getWebSocketAuthParams$7,
	handleAuthenticationError: handleAuthenticationError$7
};

//#endregion
//#region src/auth/internal/default-auth-modules/noauth.ts
function getRestCallAuthParams$6(_props) {
	return Promise.resolve({
		headers: {},
		queryParams: {},
		credentials: "same-origin"
	});
}
function getWebSocketAuthParams$6(_props) {
	return Promise.resolve({ queryParams: {} });
}
function handleAuthenticationError$6(_props) {
	return Promise.resolve({});
}
/** @private */
var noauth_default = {
	requiredProps: [],
	optionalProps: [],
	getRestCallAuthParams: getRestCallAuthParams$6,
	getWebSocketAuthParams: getWebSocketAuthParams$6,
	handleAuthenticationError: handleAuthenticationError$6
};

//#endregion
//#region src/auth/internal/default-auth-modules/none.ts
function getRestCallAuthParams$5() {
	return Promise.resolve({
		headers: {},
		queryParams: {},
		credentials: "same-origin"
	});
}
function getWebSocketAuthParams$5() {
	return Promise.resolve({ queryParams: {} });
}
function handleAuthenticationError$5() {
	return Promise.resolve({});
}
/** @private */
var none_default = {
	requiredProps: [],
	optionalProps: [],
	getRestCallAuthParams: getRestCallAuthParams$5,
	getWebSocketAuthParams: getWebSocketAuthParams$5,
	handleAuthenticationError: handleAuthenticationError$5
};

//#endregion
//#region src/auth/internal/default-auth-modules/oauth/callback.ts
/**
* Receives the oauth code and state callback query parameters,
* verifies the state, saves the code in session storage, and then
* redirects the page to the final href if it is set and differes from current uri
*/
function handleOAuthCallback() {
	const urlParams = new URLSearchParams(globalThis.location.search);
	const callbackCode = urlParams.get("code") || void 0;
	const callbackState = urlParams.get("state") || void 0;
	if (urlParams.get("error")) {
		const element = document.createElement("pre");
		element.innerText = `<code>${JSON.stringify({
			error: urlParams.get("error"),
			error_code: urlParams.get("error_code"),
			error_description: urlParams.get("error_description"),
			error_detail: urlParams.get("error_detail"),
			error_uri: urlParams.get("error_uri")
		})}</code>`;
		document.body.prepend(element);
	}
	const topic = loadAndDeleteFromSessionStorage("", "client-in-progress");
	if (topic && callbackCode && callbackState) {
		const stateFromLocalStorage = loadAndDeleteFromSessionStorage(topic, "state");
		const finalRedirectUri = loadAndDeleteFromSessionStorage(topic, "href");
		if (stateFromLocalStorage && stateFromLocalStorage === callbackState && finalRedirectUri) {
			saveInSessionStorage(topic, "code", callbackCode);
			if (finalRedirectUri !== globalThis.location.href) globalThis.location.replace(finalRedirectUri);
		}
	}
}

//#endregion
//#region src/auth/internal/default-auth-modules/oauth.ts
if (isBrowser()) handleOAuthCallback();
/**
* Retries the passed in function after calling handleAuthenticationError
*/
async function handlePotentialAuthenticationErrorAndRetry(hostConfig, fn) {
	try {
		return await fn();
	} catch (err) {
		const { retry } = await handleAuthenticationError$4({
			hostConfig,
			canRetry: true
		});
		if (retry) return fn();
		throw err;
	}
}
async function getRestCallAuthParams$4({ hostConfig }) {
	return {
		headers: { Authorization: `Bearer ${await getOAuthAccessToken(hostConfig)}` },
		queryParams: {},
		credentials: "omit"
	};
}
async function getWebSocketAuthParams$4({ hostConfig }) {
	if (isNode()) return { headers: { Authorization: `Bearer ${await getOAuthAccessToken(hostConfig)}` } };
	return { queryParams: { accessToken: await handlePotentialAuthenticationErrorAndRetry(hostConfig, async () => {
		return exchangeAccessTokenForTemporaryToken(hostConfig, await getOAuthAccessToken(hostConfig), "websocket");
	}) } };
}
async function getWebResourceAuthParams$1({ hostConfig }) {
	return { queryParams: { accessToken: await handlePotentialAuthenticationErrorAndRetry(hostConfig, async () => {
		return exchangeAccessTokenForTemporaryToken(hostConfig, await getOAuthAccessToken(hostConfig), "webresource");
	}) } };
}
async function handleAuthenticationError$4({ hostConfig }) {
	if (hostConfig.getAccessToken) {
		clearStoredOauthTokens(hostConfig);
		return {
			preventDefault: false,
			retry: true
		};
	}
	if (hostConfig.performInteractiveLogin) {
		clearStoredOauthTokens(hostConfig);
		return { retry: true };
	}
	if (isBrowser()) {
		await requestRedirect(hostConfig);
		startFullPageLoginFlow(hostConfig);
		return { preventDefault: true };
	}
	await refreshAccessToken(hostConfig);
	return {
		preventDefault: false,
		retry: true
	};
}
var oauth_default = {
	requiredProps: ["clientId"],
	optionalProps: [
		"clientSecret",
		"redirectUri",
		"accessTokenStorage",
		"scope",
		"subject",
		"userId",
		"noCache",
		"getAccessToken",
		"performInteractiveLogin"
	],
	getRestCallAuthParams: getRestCallAuthParams$4,
	getWebSocketAuthParams: getWebSocketAuthParams$4,
	getWebResourceAuthParams: getWebResourceAuthParams$1,
	handleAuthenticationError: handleAuthenticationError$4
};

//#endregion
//#region src/auth/internal/default-auth-modules/reference.ts
function getRestCallAuthParams$3() {
	throw new Error("getRestCallAuthParams should never be called for reference auth module");
}
function getWebSocketAuthParams$3() {
	throw new Error("getWebSocketAuthParams should never be called for reference auth module");
}
function handleAuthenticationError$3() {
	throw new Error("handleAuthenticationError should never be called for reference auth module");
}
/**
* This auth module is only here to provide a reference for the auth type "reference". It should never be used in practice.
*  @private */
var reference_default = {
	requiredProps: ["reference"],
	optionalProps: [],
	getRestCallAuthParams: getRestCallAuthParams$3,
	getWebSocketAuthParams: getWebSocketAuthParams$3,
	handleAuthenticationError: handleAuthenticationError$3
};

//#endregion
//#region src/auth/internal/default-auth-modules/windows-cookie/xrf-keys.ts
const xrfKeys = {};
/**
* @returns {string} 16 character long xrf-key
*/
function createXrfKey() {
	let result = "";
	for (let i = 0; i < 16; i += 1) {
		const j = Math.floor(Math.random() * 62);
		if (j < 10) result += j;
		else if (j > 9 && j < 36) result += String.fromCharCode(j + 55);
		else result += String.fromCharCode(j + 61);
	}
	return result;
}
function getXrfKey(hostConfig) {
	const locationUrl = toValidLocationUrl(hostConfig);
	xrfKeys[locationUrl] = xrfKeys[locationUrl] || createXrfKey();
	return xrfKeys[locationUrl];
}

//#endregion
//#region src/auth/internal/default-auth-modules/windows-cookie.ts
async function getRestCallAuthParams$2({ hostConfig }) {
	return {
		headers: { "X-Qlik-XrfKey": getXrfKey(hostConfig) },
		queryParams: { xrfkey: getXrfKey(hostConfig) },
		credentials: internalGetCredentialsForCookieAuth(hostConfig)
	};
}
async function getWebSocketAuthParams$2({ hostConfig }) {
	return { queryParams: {
		xrfkey: getXrfKey(hostConfig),
		"qlik-csrf-token": await getCsrfToken(hostConfig, true)
	} };
}
async function handleAuthenticationError$2({ hostConfig }) {
	if (hostConfig.loginUri) {
		await requestRedirect(hostConfig);
		globalThis.location.replace(hostConfig.loginUri.replace("{location}", encodeURIComponent(globalThis.location.href)));
		return { preventDefault: true };
	}
	if (hostConfig.getAccessToken) {
		const token = await resolveTokenIfPresent(hostConfig.getAccessToken);
		if (token) {
			await fetch(`${toValidLocationUrl(hostConfig)}/qlik-embed/main.js`, {
				method: "GET",
				mode: "cors",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`
				},
				cache: "no-cache"
			});
			return { retry: true };
		}
	}
	return {};
}
/** @private */
var windows_cookie_default = {
	requiredProps: [],
	optionalProps: [
		"loginUri",
		"crossSiteCookies",
		"getAccessToken"
	],
	getRestCallAuthParams: getRestCallAuthParams$2,
	getWebSocketAuthParams: getWebSocketAuthParams$2,
	handleAuthenticationError: handleAuthenticationError$2
};

//#endregion
//#region src/auth/internal/default-auth-modules/windows-cookie-node.ts
const nodeCookieStore = /* @__PURE__ */ new Map();
function clearCookieInStore(hostConfig) {
	const key = serializeHostConfig(hostConfig);
	nodeCookieStore.delete(key);
}
function setCookieInStore(hostConfig, cookie) {
	const key = serializeHostConfig(hostConfig);
	nodeCookieStore.set(key, cookie);
}
function getCookieInStore(hostConfig) {
	const key = serializeHostConfig(hostConfig);
	return nodeCookieStore.get(key);
}
async function getCookie(hostConfig) {
	let cookie = getCookieInStore(hostConfig);
	if (!cookie) {
		const token = await resolveTokenIfPresent(hostConfig.getAccessToken);
		if (!token) throw new Error("Failed to acquire access token");
		cookie = (await fetch(`${toValidLocationUrl(hostConfig)}/qlik-embed/main.js`, {
			method: "GET",
			mode: "cors",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`
			}
		})).headers.get("Set-Cookie") || void 0;
		if (cookie) setCookieInStore(hostConfig, cookie);
	}
	if (!cookie) throw new Error("Failed to exchange access token for cookie");
	return cookie;
}
async function getRestCallAuthParams$1({ hostConfig }) {
	return {
		queryParams: { xrfkey: getXrfKey(hostConfig) },
		headers: {
			Cookie: await getCookie(hostConfig),
			"X-Qlik-XrfKey": getXrfKey(hostConfig)
		},
		credentials: internalGetCredentialsForCookieAuth(hostConfig)
	};
}
async function getWebSocketAuthParams$1({ hostConfig }) {
	return {
		queryParams: { "qlik-csrf-token": await getCsrfToken(hostConfig, true) },
		headers: { Cookie: await getCookie(hostConfig) }
	};
}
async function handleAuthenticationError$1({ hostConfig }) {
	clearCookieInStore(hostConfig);
	return { retry: true };
}
/** @private */
var windows_cookie_node_default = {
	requiredProps: [],
	optionalProps: [
		"loginUri",
		"crossSiteCookies",
		"getAccessToken"
	],
	validateHostConfig: (hostConfig) => {
		if (typeof hostConfig.getAccessToken === "function") return true;
		if (typeof hostConfig.getAccessToken === "string") {
			if (!!!globalThis[hostConfig.getAccessToken]) throw new InvalidHostConfigError("The \"getAccessToken\" function name provided does not exist on globalThis.");
			return true;
		}
		throw new InvalidHostConfigError("The \"getAccessToken\" property must be a function or the name of a globally defined function.");
	},
	getRestCallAuthParams: getRestCallAuthParams$1,
	getWebSocketAuthParams: getWebSocketAuthParams$1,
	handleAuthenticationError: handleAuthenticationError$1
};

//#endregion
//#region src/auth/internal/auth-module-registry.ts
/**
* Auth module registry
*/
const authModules = {};
/**
* A promise that gets recreated when an auth module is currently being loaded
*/
let ongoingAuthModuleLoading = Promise.resolve();
let authModulesRegistered = false;
(function registerDefaultAuthModules() {
	if (!authModulesRegistered) {
		registerAuthModule$1("apikey", apikey_default);
		registerAuthModule$1("cookie", cookie_default);
		registerAuthModule$1("none", none_default);
		registerAuthModule$1("noauth", noauth_default);
		registerAuthModule$1("oauth2", oauth_default);
		registerAuthModule$1("anonymous", anonymous_default);
		if (isBrowser()) registerAuthModule$1("windowscookie", windows_cookie_default);
		else registerAuthModule$1("windowscookie", windows_cookie_node_default);
		registerAuthModule$1("reference", reference_default);
		authModulesRegistered = true;
	}
})();
/**
* Registers an auth module that can handle authentication. An auth module is used by specifying its name as authType in the HostConfig passed in to api calls.
* @param name the name of the module
* @param authModule the implementation of the AuthModule interface
*/
function registerAuthModule$1(name, authModule) {
	authModules[name.toLowerCase()] = authModule;
}
/**
* Get the names of all registered AuthModules.
* @returns Array of all registered AuthModules
*/
function getRegisteredAuthModules() {
	return Object.keys(authModules);
}
/**
*  Gets the auth module with the given name. The name is case insensitive.
* @param authType The name of the auth module to get. The name is case insensitive.
* @returns
*/
function getRegisteredAuthModule(authType) {
	return authModules[authType.toLowerCase()];
}
/**
* Gets the auth module for a host config. If only clientId is set, then authType is assumed and set to "oauth2".
* If host config is undefined or empty, then authType is assumed and set to "cookie" or "windowscookie" depending
*  on which platform the host is running on.
*
* @private
* @param hostConfig
*/
async function getAuthModule(hostConfig) {
	const hostConfigToUse = resolveHostConfig(hostConfig);
	const authType = await determineAuthType$1(hostConfigToUse);
	if (ongoingAuthModuleLoading) await ongoingAuthModuleLoading;
	let authModule = getRegisteredAuthModule(authType);
	if (!authModule) {
		/**
		* This is basically poor mans synchronous block so that one globally defined asynchronous auth module factory function
		* is not called more than once if several calls to getAuthModule come in in sequence.
		*/
		ongoingAuthModuleLoading = (async () => {
			authModule = await resolveGloballyDefinedAuthModule(authType);
			if (authModule) registerAuthModule$1(authType, authModule);
		})();
		await ongoingAuthModuleLoading;
	}
	if (!authModule) throw new InvalidAuthTypeError(authType);
	if (authModule.validateHostConfig) authModule.validateHostConfig({
		authType,
		...hostConfigToUse
	});
	else internalValidateHostConfig({
		authType,
		...hostConfigToUse
	}, {
		requiredProps: authModule.requiredProps || [],
		optionalProps: authModule.optionalProps || []
	});
	return authModule;
}
async function resolveGloballyDefinedAuthModule(authType) {
	const globalVariable = globalThis[authType];
	if (globalVariable) {
		let potentialAuthModule;
		if (typeof globalVariable === "function") potentialAuthModule = await globalVariable();
		else potentialAuthModule = globalVariable;
		if (potentialAuthModule && potentialAuthModule.getRestCallAuthParams && potentialAuthModule.getWebSocketAuthParams && potentialAuthModule.handleAuthenticationError) return potentialAuthModule;
		console.error("Not a valid auth module", potentialAuthModule);
		throw new InvalidAuthTypeError(authType);
	}
	return Promise.resolve(void 0);
}
/**
* @private
* Helper function for validating a host config in runtime
* @param hostConfig - The host config to validate
* @param options - Options for validation
* @param options.requiredProps - The required properties in the host config
* @param options.optionalProps - The optional properties in the host config
* @throws {InvalidHostConfigError} If the host config is invalid
* @returns {boolean} Returns true if the host config is valid
* */
function internalValidateHostConfig(hostConfig, { requiredProps, optionalProps }) {
	const missingRequiredProps = [];
	for (const requiredProp of requiredProps) if (!hostConfig[requiredProp]) missingRequiredProps.push(requiredProp);
	if (missingRequiredProps.length > 0) throw new InvalidHostConfigError(`missing required properties in host config; '${missingRequiredProps.join("', '")}'`);
	const validProps = [
		...hostConfigCommonProperties,
		...requiredProps,
		...optionalProps
	];
	const invalidKeys = [];
	Object.keys(hostConfig).forEach((key) => {
		if (!validProps.includes(key)) invalidKeys.push(key);
	});
	if (invalidKeys.length > 0) console.warn(`WARNING: unknown properties in host config; '${invalidKeys.join("', '")}'`);
	return true;
}
/**
* Determines the authType associated with a HostConfig even if it's
* not explicitly set.
*/
async function determineAuthType$1(hostConfig) {
	if (hostConfig.authType) return hostConfig.authType;
	if (hostConfig.apiKey) return "apikey";
	if (hostConfig.accessCode) return "anonymous";
	if (hostConfig.clientId) return "oauth2";
	if (hostConfig.webIntegrationId) return "cookie";
	if (hostConfig.reference) return "reference";
	if (await isWindows(hostConfig)) return "windowscookie";
	return "cookie";
}

//#endregion
//#region src/auth/auth-errors.ts
/** Error is thrown when a host config has incorrect or missing settings */
var InvalidHostConfigError = class extends Error {
	constructor(message) {
		super(`Invalid host config: ${message}`);
		this.name = "InvalidHostConfigError";
	}
};
/** Error is thrown when a host config has an auth type with incompatible settings */
var UnexpectedAuthTypeError = class extends Error {
	constructor(...expectedAuthTypes) {
		const ors = expectedAuthTypes.map((item, index) => index === 0 ? `"${item}"` : `or "${item}"`).join(" ");
		super(`HostConfig is not properly configured. authType is expected to be ${ors}`);
		this.name = "UnexpectedAuthTypeError";
	}
};
/** Error is thrown when a host config uses an auth type that has not been registered */
var InvalidAuthTypeError = class extends Error {
	constructor(authType) {
		const validAuthModules = getRegisteredAuthModules();
		super(`Not a valid auth type: ${authType}, valid auth types are; '${validAuthModules.filter((name) => name.toLowerCase() !== "qmfeembedframerauthmodule").join("', '")}'`);
		this.name = "InvalidAuthTypeError";
	}
};
function errorToString({ title, detail, code, status }) {
	if (detail) return `${title} - ${detail} (Status: ${status}, Code: ${code})`;
	return `${title} (Status: ${status}, Code: ${code})`;
}
var AuthorizationError = class extends Error {
	errors;
	constructor(errors) {
		if (typeof errors !== "object") {
			super("Unknown error");
			return;
		}
		const errorArray = Array.isArray(errors) ? errors : [errors];
		super(errorArray.map(errorToString).join(", "));
		this.errors = errorArray;
	}
};

//#endregion
//#region src/auth/internal/fatal-auth-error-listeners.ts
const listenerRegistries = /* @__PURE__ */ new Map();
let lastErrorMessage = "";
let lastHostConfig;
/**
* Retrieves the listener registry for a given host configuration, creating one if it doesn't exist.
*/
function getAuthErrorListenerRegistryForHostConfig(hostConfig) {
	const key = serializeHostConfig$1(hostConfig);
	let registry = listenerRegistries.get(key);
	if (!registry) {
		registry = { listeners: /* @__PURE__ */ new Set() };
		listenerRegistries.set(key, registry);
	}
	return registry;
}
/**
* Emits a fatal authentication error to all registered listeners plus to the onAuthFailed property in the hostConfig.
* If there are no listeners or onAuthFailed callback, the error message is logged to the console.
*/
function emitFatalAuthError(hostConfig, normalizedError) {
	if (hostConfig === lastHostConfig && normalizedError.message === lastErrorMessage) return;
	lastHostConfig = hostConfig;
	lastErrorMessage = normalizedError.message;
	let someOneIsListening = false;
	const registry = getAuthErrorListenerRegistryForHostConfig(hostConfig);
	for (const listener of registry.listeners) try {
		someOneIsListening = true;
		listener(normalizedError);
	} catch (listenerError) {
		console.warn("Error in auth error listener", listenerError);
	}
	if (hostConfig.onAuthFailed) try {
		someOneIsListening = true;
		hostConfig.onAuthFailed(normalizedError);
	} catch (callbackError) {
		console.warn("Error in onAuthFailed callback", callbackError);
	}
	if (!someOneIsListening) console.error(normalizedError.message);
}
/**
* Registers a listener for fatal auth errors. This means errors in the actual auth mechanism, i.e. misconfigurations etc.
*/
function onFatalAuthError$1(hostConfig, callback) {
	const registry = getAuthErrorListenerRegistryForHostConfig(hostConfig);
	registry.listeners.add(callback);
	return () => {
		registry.listeners.delete(callback);
	};
}

//#endregion
//#region src/auth/auth-functions.ts
/**
* Set initial loggingOut value to false to make sure it has a value before any auth module is loaded
*/
globalThis.loggingOut = false;
/**
* Determines the authType associated with a HostConfig even if it's
* not explicitly set.
* @param hostConfig the HostConfig containing authentication details
* @returns promise that resolvs with the determined authentication type
*/
function determineAuthType(hostConfig) {
	return determineAuthType$1(hostConfig);
}
/**
* Determines if a host is cross origin or not
* @param hostConfig the HostConfig containing authentication details
* @private
*/
function isHostCrossOrigin(hostConfig) {
	if (!globalThis.location?.origin) return true;
	const hostConfigToUse = resolveHostConfig(hostConfig);
	if (Object.keys(hostConfigToUse).length === 0) return false;
	try {
		return new URL(toValidLocationUrl(hostConfigToUse)).origin !== globalThis.location.origin;
	} catch {}
	return false;
}
/**
* Determines if the host we're connecting to is running on windows or not
* @param hostConfig the HostConfig containing authentication details
*/
async function isWindows(hostConfig) {
	const hostConfigToUse = resolveHostConfig(hostConfig);
	if (typeof hostConfigToUse.forceIsWindows === "boolean") return hostConfigToUse.forceIsWindows;
	if (hostConfigToUse.host?.endsWith(".qlik-stage.com") || hostConfigToUse.host?.endsWith(".qlikcloud.com") || hostConfigToUse.host?.endsWith(".qlikcloudgov.com")) return false;
	if (hostConfigToUse.authType === "cookie") return false;
	if (hostConfigToUse.authType === "windowscookie") return true;
	return (await getPlatform({ hostConfig })).isWindows;
}
/**
* Returns a valid location url based on the supplied location string. If location is undefined the default is returned
* @param hostConfig the HostConfig containing authentication details
*/
function toValidLocationUrl(hostConfig) {
	const url = resolveHostConfig(hostConfig)?.host?.trim();
	let locationUrl;
	if (!url) locationUrl = "";
	else if (url.toLowerCase().startsWith("https://") || url.toLowerCase().startsWith("http://")) locationUrl = url;
	else locationUrl = `https://${url}`;
	while (locationUrl[locationUrl.length - 1] === "/") locationUrl = locationUrl.substring(0, locationUrl.length - 1);
	return locationUrl;
}
/**
* Returns a valid websocket location url based on the supplied location string. If location is undefined the default is returned
* @param hostConfig the HostConfig containing authentication details
*/
function toValidWebsocketLocationUrl(hostConfig) {
	const url = resolveHostConfig(hostConfig)?.host;
	let locationUrl;
	if (!url) locationUrl = globalThis.location.origin;
	else if (url.toLowerCase().startsWith("https://") || url.toLowerCase().startsWith("http://")) locationUrl = url;
	else locationUrl = `https://${url}`;
	while (locationUrl[locationUrl.length - 1] === "/") locationUrl = locationUrl.substring(0, locationUrl.length - 1);
	return locationUrl.replace(leadingHttp, "ws");
}
/**
* Returns a record of query parameters that needs to be added to websockets
* @param hostConfig the HostConfig containing authentication details
*/
async function getWebSocketAuthParams(props) {
	const hostConfigToUse = resolveHostConfig(props.hostConfig);
	try {
		return await (await getAuthModule(hostConfigToUse)).getWebSocketAuthParams({
			...props,
			hostConfig: hostConfigToUse
		});
	} catch (err) {
		emitFatalAuthError(hostConfigToUse, normalizeAuthModuleError(err));
		throw err;
	}
}
/**
* Returns a record of query parameters that needs to be added to resources requests, e.g.
* image tags, etc.
* @param hostConfig the HostConfig containing authentication details
*/
async function getWebResourceAuthParams(props) {
	const hostConfigToUse = resolveHostConfig(props.hostConfig);
	try {
		return await (await getAuthModule(hostConfigToUse)).getWebResourceAuthParams?.({
			...props,
			hostConfig: hostConfigToUse
		}) || { queryParams: {} };
	} catch (err) {
		emitFatalAuthError(hostConfigToUse, normalizeAuthModuleError(err));
		throw err;
	}
}
/**
* Calls and return handleAuthenticationError on the authModule a host config is associated with.
*/
async function handleAuthenticationError(props) {
	const hostConfigToUse = resolveHostConfig(props.hostConfig);
	const result = await (await getAuthModule(hostConfigToUse)).handleAuthenticationError({
		...props,
		hostConfig: hostConfigToUse
	});
	const willRetry = props.canRetry && result.retry;
	const willHangUntilANewPageIsLoaded = result.preventDefault;
	if (!willRetry && !willHangUntilANewPageIsLoaded) {
		const { status, errorBody } = props;
		emitFatalAuthError(hostConfigToUse, normalizeInbandAuthError({
			status,
			errorBody
		}));
	}
	return result;
}
/**
* Returns a record of headers and a record of query params that needs to be added to outgoing rest calls
* @param props.hostConfig the HostConfig containing authentication details
* @param props.method the http method, which may affect what authentication are needed.
*/
async function getRestCallAuthParams(props) {
	const hostConfigToUse = resolveHostConfig(props.hostConfig);
	try {
		return await (await getAuthModule(hostConfigToUse)).getRestCallAuthParams({
			...props,
			hostConfig: hostConfigToUse
		});
	} catch (err) {
		emitFatalAuthError(hostConfigToUse, normalizeAuthModuleError(err));
		throw err;
	}
}
/**
* Returns a record of headers and a record of query params that needs to be added to outgoing rest calls
* @param props.hostConfig the HostConfig containing authentication details
* @param props.method the http method, which may affect what authentication are needed.
*/
async function getAccessToken(props) {
	const authorizationHeader = (await getRestCallAuthParams({
		method: "GET",
		...props
	})).headers?.Authorization;
	if (authorizationHeader.indexOf("Bearer ") === 0) return authorizationHeader.substring(7);
	throw new Error("Unknown format of authorization header returned by remote auth module");
}
/**
* Registers an auth module that can handle authentication. An auth module is used by specifying its name as authType in the HostConfig passed in to api calls.
* @param name the name of the module
* @param authModule the implementation of the AuthModule interface
*/
function registerAuthModule(name, authModule) {
	registerAuthModule$1(name, authModule);
}
/**
* Sets the default host config that will be used for all api calls that do not include a HostConfig
* @param hostConfig the default HostConfig to use
*/
function setDefaultHostConfig(hostConfig) {
	setDefaultHostConfig$1(hostConfig);
}
/**
* Registers a host config with the given name.
* @param name The name of the host config to be used to reference the host config later.
* @param hostConfig The host config to register.
*/
function registerHostConfig(name, hostConfig) {
	registerHostConfig$1(name, hostConfig);
}
/**
* Unregisters a host config with the given name.
* @param name The name of the host config to unregister.
*/
function unregisterHostConfig(name) {
	unregisterHostConfig$1(name);
}
/**
* Serializes the provided hostConfig, if present, otherwise the default one.
*/
function serializeHostConfig(hostConfig) {
	return serializeHostConfig$1(hostConfig);
}
/**
* Throws errors if hostConfig is missing or missing properties on cross domain requests
*/
function checkForCrossDomainRequest(hostConfig) {
	const hostConfigToUse = resolveHostConfig(hostConfig);
	if (isHostCrossOrigin(hostConfigToUse)) {
		if (Object.keys(hostConfigToUse).length === 0) throw new InvalidHostConfigError("a host config must be provided when making a cross domain request");
		if (!hostConfigToUse.host) throw new InvalidHostConfigError("A 'host' property must be set in host config when making a cross domain request");
	}
}
/**
* Returns a normalized host config that will always be the same instance when the serialized host config is the same.
*/
function normalizeHostConfig(hostConfig) {
	return normalizeHostConfig$1(hostConfig);
}
/**
* Logs out the user and sets `global.loggingOut` to true.
* **NOTE**: Does not abort pending requests.
*/
const logout = () => {
	globalThis.loggingOut = true;
	globalThis.location.href = "/logout";
};
const leadingHttp = /^http/;
/**
* Normalizes the error object returned from the auth module to a FatalAuthError
* @param errorBody The error body to normalize
* @param status The status code of the error
* @returns Either the error message or a default message
* @private
*/
function normalizeInbandAuthError({ errorBody, status }) {
	const authError = errorBody;
	if (typeof authError?.errors === "object") return { message: new AuthorizationError(authError?.errors).message };
	return { message: `HTTP ${status}` };
}
/**
* Normalizes the error object returned from the auth module to a FatalAuthError
* @param err The error to normalize
* @returns Either the error message or a default message
* @private
*/
function normalizeAuthModuleError(err) {
	return { message: err.message || "Unknown error" };
}
/**
* Replaces the supplied host config according the following rules:
* * If the host config is a reference to a registered host config, the registered host config will be used.
* * If the host config is undefined or empty, the default host config will be returned.
* * If the host config is not a reference and not empty or undefined, it will be returned as is.
* * If the host config is empty or undefined an empty object (which represents the default cookie mode) is returned.
* @param hostConfig - The host config to resolve
*/
function resolveHostConfig(hostConfig) {
	if (hostConfig?.reference) {
		const refConfig = getRegisteredHostConfig(hostConfig.reference);
		if (!refConfig) throw new InvalidHostConfigError(`Host config with name "${hostConfig.reference}" not found.`);
		return refConfig;
	}
	if (hostConfig && Object.keys(hostConfig).length > 0) return hostConfig;
	return getDefaultHostConfig$1();
}
/**
* Returns the default host config that is used for all qmfe api calls that do not include a host config
*/
function getDefaultHostConfig() {
	return getDefaultHostConfig$1();
}
/**
* Registers a callback to be invoked when a fatal authentication error occurs for the provided hostConfig.
* @param callback The callback function to register
* @returns A function to unregister the callback
*/
function onFatalAuthError(hostConfig, callback) {
	return onFatalAuthError$1(hostConfig, callback);
}
/**
* Registers a listener for page redirect requested by the auth module that is handling authentication for the provided host configuration.
* This is typically used when embedded to let the end user explicitly approve a redirect by for instance clicking an "authorize" button. The user can then decide to leave that UI unauthorized
* and stay on the page.
* The listener is provided a `proceed` function that if called continues the redirect process. Before actually redirecting the page, the onPageRedirectStarted listeners are called.
* Note that if more than one listener is registered, it still takes just one proceed call to continue the redirect process so it can not be used to block the redirect.
*/
function onPageRedirectRequested(hostConfig, listener) {
	return onPageRedirectRequested$1(hostConfig, listener);
}
/**
* Registers a listener that will be called when a page redirect is started by the auth module that is handling authentication for the provided host configuration.
*/
function onPageRedirectStarted(hostConfig, listener) {
	return onPageRedirectStarted$1(hostConfig, listener);
}

//#endregion
//#region src/interceptors/boot-interceptors.ts
/**
* @internal
* The paths expected in the from core-init BFF response.
* Exposed for testing.
*/
const coreBootPaths = new Set([
	"/api/v1/brands/active",
	"/api/v1/claims/me",
	"/api/v1/licenses/allotments",
	"/api/v1/licenses/allotments/me",
	"/api/v1/licenses/number",
	"/api/v1/licenses/status",
	"/api/v1/tenant-settings",
	"/api/v1/tenants/me",
	"/api/v1/user-settings",
	"/api/v1/users/me",
	"/api/v1/user-locale"
]);
/**
* @internal
* The path of the core-init BFF response.
* Exposed for testing.
*/
const coreBootEndpoint = "/api/v1/boot/core-init";
let coreBootPromises = {};
let enableDespiteNode = false;
function interceptorEnabled() {
	return !isNode() || enableDespiteNode;
}
/**
* An interceptor that populates the cache from the response of a _backend
* for frontend_ endpoint. This endpoint contains the response of the most common
* initial calls, mainly from **qmfe-core**.
*
* If the interceptor finds a matching call it runs once and then removes itself after
* populating the cache causing the original request (and following ones) to hit the cache instead.
*
* The endpoint is implemented in the _customizations_ service and should
* contain the responses of the requests specified in 'coreBootPaths'.
*/
const coreBootInterceptor = async (request, proceed) => {
	if (!interceptorEnabled()) return proceed(request);
	if (request.pathTemplate !== "/api/v1/features" && !coreBootPaths.has(request.pathTemplate)) return proceed(request);
	const hostConfig = request.options?.hostConfig;
	if (hostConfig?.authType === "noauth" || await isWindows(hostConfig) || (await getPlatform({ hostConfig })).isCloudConsole) return proceed(request);
	const serializedHostConfig = serializeHostConfig$1(request.options?.hostConfig);
	coreBootPromises[serializedHostConfig] ??= callCoreBootAndFillCache(hostConfig, serializedHostConfig);
	if (request.pathTemplate !== "/api/v1/features") await coreBootPromises[serializedHostConfig];
	return proceed(request);
};
async function callCoreBootAndFillCache(hostConfig, serializedHostConfig) {
	const bootRequest = {
		method: "GET",
		pathTemplate: coreBootEndpoint,
		options: { hostConfig }
	};
	try {
		const bootResponse = await invokeFetch(".global", bootRequest);
		const locationUrl = toValidLocationUrl(hostConfig);
		populateCacheFrom(bootResponse.data, coreBootPaths, serializedHostConfig, locationUrl);
	} catch (e) {
		console.error("Failed to bootstrap core-info:", e);
	}
}
function populateCacheFrom(responses, pathsToInclude, serializedHostConfig, locationUrl) {
	if (!responses) return;
	for (const res of responses) {
		if (!pathsToInclude.has(res.path)) continue;
		const completeUrl = locationUrl + res.path;
		const { status, method, data } = res;
		if (typeof data === "undefined") continue;
		if (status && (status < 200 || status >= 300)) continue;
		const response = Promise.resolve({
			status: status || 200,
			data
		});
		const cacheKey = toCacheKey({
			url: completeUrl,
			serializedHostConfig
		});
		updateCache(globalCacheNamespace, {
			method: method || "GET",
			completeUrl,
			cacheKey
		}, response);
	}
}

//#endregion
//#region src/interceptors/interceptors.ts
let GLOBAL_INTERCEPTORS;
function createInterceptors() {
	const interceptors = [...GLOBAL_INTERCEPTORS?.getInterceptors() || []];
	return {
		addInterceptor: (interceptor) => {
			interceptors.push(interceptor);
			return interceptor;
		},
		removeInterceptor: (interceptor) => {
			const index = interceptors.indexOf(interceptor);
			let removed;
			if (index !== -1) removed = interceptors.splice(index, 1)[0];
			return removed || null;
		},
		getInterceptors: () => interceptors
	};
}
let addDefaultInterceptorsRun = false;
function addDefaultInterceptors() {
	if (addDefaultInterceptorsRun) return;
	if (isBrowser()) {
		const readFlagsFromUrlQuery = () => {
			const featuresParam = new URLSearchParams(window.location.search).get("features");
			if (!featuresParam) return {};
			return featuresParam.split(",").map((item) => item.trim()).reduce((map, obj) => {
				const value = !obj.startsWith("!");
				const key = value ? obj : obj.substring(1);
				map[key] = value;
				return map;
			}, {});
		};
		const readFlagsFromLocalStorage = () => {
			try {
				const featuresParam = localStorage.getItem("qcs-features");
				if (featuresParam) return JSON.parse(featuresParam);
				return {};
			} catch {
				return {};
			}
		};
		const flagsFromUrl = readFlagsFromUrlQuery();
		const flagsFromLocalStorage = readFlagsFromLocalStorage();
		const featuresInterceptor = async (request, proceed) => {
			let resultPromise;
			if (request.pathTemplate === "/api/v1/features") {
				resultPromise = proceed(request);
				const result = await resultPromise;
				return {
					...result,
					data: {
						...result.data || {},
						...flagsFromLocalStorage,
						...flagsFromUrl
					}
				};
			}
			return proceed(request);
		};
		GLOBAL_INTERCEPTORS.addInterceptor(featuresInterceptor);
		GLOBAL_INTERCEPTORS.addInterceptor(coreBootInterceptor);
	}
	addDefaultInterceptorsRun = true;
}
/**
* The global interceptor stack
*/
GLOBAL_INTERCEPTORS = createInterceptors();
/**
* Adds an interceptor to the global interceptor stack
* Returns the newly added interceptor
* @param interceptor the interceptor to add
* @returns the newly added interceptor
*/
function addInterceptor(interceptor) {
	return GLOBAL_INTERCEPTORS.addInterceptor(interceptor);
}
/**
* Removes an interceptor from the global interceptor stack
* @param interceptor the interceptor remove
*/
function removeInterceptor(interceptor) {
	return GLOBAL_INTERCEPTORS.removeInterceptor(interceptor);
}
/**
* Gets all registered interceptors
*/
function getInterceptors() {
	return GLOBAL_INTERCEPTORS.getInterceptors();
}
/**
* The interceptors API
*/
const interceptors = {
	addInterceptor,
	removeInterceptor,
	getInterceptors,
	createInterceptors
};

//#endregion
export { InvalidHostConfigError as A, getPlatform as B, serializeHostConfig as C, unregisterHostConfig as D, toValidWebsocketLocationUrl as E, appendQueryToUrl as F, EncodingError as I, InvokeFetchError as L, clearApiCache as M, invokeFetch as N, AuthorizationError as O, parseFetchResponse as P, exposeInternalApiOnWindow as R, registerHostConfig as S, toValidLocationUrl as T, normalizeHostConfig as _, interceptors as a, onPageRedirectStarted as b, getAccessToken as c, getWebResourceAuthParams as d, getWebSocketAuthParams as f, logout as g, isWindows as h, getInterceptors as i, UnexpectedAuthTypeError as j, InvalidAuthTypeError as k, getDefaultHostConfig as l, isHostCrossOrigin as m, addInterceptor as n, removeInterceptor as o, handleAuthenticationError as p, createInterceptors as r, determineAuthType as s, addDefaultInterceptors as t, getRestCallAuthParams as u, onFatalAuthError as v, setDefaultHostConfig as w, registerAuthModule as x, onPageRedirectRequested as y, generateRandomString as z };